import React, { useState, useEffect, createContext, useContext, useCallback, useRef, Component, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import MaintenanceOverlay from "./components/common/MaintenanceOverlay";
import StickyGetAppBar from "./components/common/StickyGetAppBar";
import BuildVersionChecker from "./components/common/BuildVersionChecker";
import ProfileCompleteGate from "./components/common/ProfileCompleteGate";
import { EmergeBlockPage, EmergeTxPage, EmergeDropPage } from "./pages/EmergeExplorerPage";
import SEOHead from "./components/common/SEOHead";
import ModalPortal from "./components/common/ModalPortal";
import MyClaimsStatus from "./components/common/MyClaimsStatus";
import { Menu, X, Wallet, Play, Store, User, Gift, ShoppingCart, Flame, Search, RefreshCw, DollarSign, TrendingUp, TrendingDown, Users, Package, CreditCard, ArrowUpRight, ArrowDownRight, Clock, Check, XCircle, Send, LogOut, ChevronRight, ChevronLeft, Star, Sparkles, Info, Shield, Zap, Heart, Bell, Share2, Plus, Lock, MessageCircle, Calendar, Hand, Download, Video, Minus, Layers, Trophy, Target, Coins, AlertCircle, Upload, FileText, Mail } from "lucide-react";
import { toast, Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Import critical components directly (needed for initial render)
import { TermsPage, PrivacyPage, CopyrightPage, LicensingPage } from "./pages/LegalPages";
import { AboutPage, ContactPage, FAQPage, ShippingPage } from "./pages/BusinessPages";
import WhatsNewPage from "./components/pages/WhatsNewPage";
import PartnershipsPage from "./components/home/PartnershipTiers";
import ClaimYourCardPage from "./pages/ClaimYourCardPage";
import UnsubscribePage from "./components/pages/UnsubscribePage";
import Footer from "./components/layout/Footer";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import MaintenanceGuard from "./components/common/MaintenanceGuard";
// GetAppBanner — removed from render tree 2026-05-15. Import kept commented
// for future restore if needed. See note where it was rendered for context.
// import GetAppBanner from "./components/common/GetAppBanner";
// SplashIntro removed — kept native Capacitor splash; no custom logo+flame intro.
import { trackConversion } from "./utils/analytics";
import { getMediaUrl, formatApiError } from "./utils";
import usePerformanceMode from "./hooks/usePerformanceMode";
import useLiveNowBadge from "./hooks/useLiveNowBadge";
import useVersionWatcher from "./hooks/useVersionWatcher";
import { configureStatusBar, setupAppLifecycle, isNative, saveCredentials, getCredentials, haptic, hideNativeSplash } from "./utils/nativeBridge";
// LaunchSplash removed 2026-05-15 — see explanatory comment further down.
import { initNetworkWatcher } from "./utils/offlineCache";

// DIRECT IMPORT for HomePage - must load immediately, no lazy loading
// This fixes PWA/TWA black screen issue where lazy loading fails silently
import HomePageComponent from "./components/pages/HomePage";

// LAZY LOAD heavy page components for faster initial load (mobile optimization)
// NOTE: HomePage is NOT lazy-loaded - it's imported directly above for PWA/TWA compatibility
const StakingPageComponent = lazy(() => import("./components/pages/StakingPage"));
const ChallengesPageComponent = lazy(() => import("./components/pages/ChallengesPage"));
const PlayPageComponent = lazy(() => import("./components/pages/PlayPage"));
const SellPageComponent = lazy(() => import("./components/pages/SellPage"));
const MonsterMeterPageComponent = lazy(() => import("./components/pages/MonsterMeterPage"));
const MarketplacePageComponent = lazy(() => import("./components/pages/MarketplacePage"));
const LiveBreaksPageComponent = lazy(() => import("./components/pages/LiveBreaksPage"));
const LiveBreakRoomPageComponent = lazy(() => import("./components/pages/LiveBreakRoomPage"));
// /host/:dropId → /live/:dropId — preserves any inbound deep links from old
// notifications, social shares, or bookmarks. Pre-recorded break model has
// no human host, so Break Control is now an admin overlay inside the Live
// Break room. See 2026-02-23 refactor.
function HostRedirect() {
  const { dropId } = useParams();
  return <Navigate to={`/live/${dropId}`} replace />;
}
const RipPageComponent = lazy(() => import("./components/pages/RipPage"));
const LockerPageComponent = lazy(() => import("./components/pages/LockerPage"));
const WalletPageComponent = lazy(() => import("./components/pages/WalletPage"));
const AdminPageComponent = lazy(() => import("./components/pages/AdminPage"));
const ProfilePageComponent = lazy(() => import("./components/pages/ProfilePage"));
const SeriesPageComponent = lazy(() => import("./components/pages/SeriesPage"));
const MySetsPageComponent = lazy(() => import("./components/pages/MySetsPage"));
const CalculatorPageComponent = lazy(() => import("./components/pages/CalculatorPage"));
const ActivityFeedPageComponent = lazy(() => import("./pages/ActivityFeedPage"));
const RunItPageComponent = lazy(() => import("./pages/RunItPage"));
const TournamentsPageComponent = lazy(() => import("./pages/TournamentsPage"));
const WatchMatchPageComponent = lazy(() => import("./pages/WatchMatchPage"));
const CardBattleHistoryPageComponent = lazy(() => import("./pages/CardBattleHistoryPage"));
const PublicProfilePageComponent = lazy(() => import("./pages/PublicProfilePage"));
const BoostCheckoutPageComponent = lazy(() => import("./components/pages/BoostCheckoutPage"));
const EventsPageComponent = lazy(() => import("./components/pages/EventsPage"));
const FirstRipPageComponent = lazy(() => import("./components/pages/FirstRipPage"));
const VerifyPageComponent = lazy(() => import("./pages/VerifyPage"));
const TxDetailPageComponent = lazy(() => import("./pages/TxDetailPage"));

// Lazy load collectible components (not needed immediately)
const PackOpeningAnimation = lazy(() => import("./components/collectibles/PackOpeningAnimation"));
const CollectibleCardComponent = lazy(() => import("./components/collectibles/CollectibleCard"));
const RotatingCubeComponent = lazy(() => import("./components/collectibles/RotatingCube"));
const AdminChallengesTabComponent = lazy(() => import("./components/admin/AdminChallengesTab"));
const DropDetailPageComponent = lazy(() => import("./components/pages/DropDetailPage"));

// Import animated price ticker components
import { CompactPriceTicker, MarqueePriceTicker, PriceDisplayCard, InlinePriceTicker } from "./components/common/AnimatedPriceTicker";

// Mobile-optimized loading spinner
const PageLoader = () => (
  <div className="min-h-screen bg-[#12121e] flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

// ScrollToTop Component - ensures page scrolls to top on navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Error Boundary Component - catches rendering errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // ── CHUNK LOAD ERROR HANDLING ──────────────────────────────────
    // This is the #1 cause of users seeing "Something went wrong" right
    // after a production deploy: their old tab references the previous
    // build's hashed JS chunks (e.g. LockerPage.abc123.js) which 404
    // because they've been replaced. We detect that specific failure
    // and force a one-shot full reload so they pull the new bundle —
    // localStorage (including auth token) is preserved, so users do NOT
    // need to log back in. A sessionStorage flag prevents an infinite
    // reload loop if the error somehow isn't a chunk problem.
    const isChunkLoadError =
      error?.name === "ChunkLoadError" ||
      /Loading chunk [\d]+ failed/i.test(error?.message || "") ||
      /Loading CSS chunk/i.test(error?.message || "") ||
      /Failed to fetch dynamically imported module/i.test(error?.message || "") ||
      /Importing a module script failed/i.test(error?.message || "");

    if (isChunkLoadError) {
      const flagKey = "__blk_chunk_reload_attempted__";
      try {
        if (!sessionStorage.getItem(flagKey)) {
          sessionStorage.setItem(flagKey, String(Date.now()));
          // Hard reload (bypass cache) — pulls the latest chunk manifest.
          // Auth token is in localStorage so the user stays logged in.
          window.location.reload();
          return;
        }
      } catch (e) {
        /* sessionStorage blocked → fall through to normal error UI */
      }
    }

    // ── STALE BUNDLE AUTO-RECOVERY ─────────────────────────────────
    // ANY uncaught render-time error in a freshly deployed environment
    // is most often caused by an OLD cached JS bundle running against a
    // backend that's already moved on (newer API shape, removed fields,
    // etc.). Before showing the user a scary "Something went wrong"
    // wall, do ONE silent hard reload. This is bounded by sessionStorage
    // so we can't infinite-loop — if the very next render also crashes,
    // we fall through to the normal error UI and the user sees the
    // "Try Again / Refresh page" buttons.
    const stalebundleFlag = "__blk_stalebundle_reload_attempted__";
    try {
      if (!sessionStorage.getItem(stalebundleFlag)) {
        sessionStorage.setItem(stalebundleFlag, String(Date.now()));
        // Unregister stale service workers so the next load isn't
        // intercepted by an old SW shell.
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.getRegistrations?.().then((regs) => {
            regs.forEach((r) => r.unregister().catch(() => {}));
          });
        }
        // Give the SW unregister calls ~150ms to dispatch, then reload.
        setTimeout(() => window.location.reload(), 150);
        return;
      }
    } catch (e) {
      /* sessionStorage blocked → fall through to normal error UI */
    }

    console.error("ErrorBoundary caught an error:", error, errorInfo);
    console.error("Error stack:", error?.stack);
    console.error("Component stack:", errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#12121e] flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="text-red-500" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-gray-400 mb-6">We encountered an unexpected error. Try again — your account is still signed in.</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  // Try again WITHOUT a hard reload — keeps user on the
                  // same route + auth state. If the same crash repeats,
                  // they can use "Refresh page" below.
                  this.setState({ hasError: false, error: null });
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-lg"
                data-testid="error-boundary-try-again"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  try { sessionStorage.removeItem("__blk_chunk_reload_attempted__"); } catch (e) {}
                  window.location.reload();
                }}
                className="text-gray-400 hover:text-white text-sm py-2"
                data-testid="error-boundary-refresh"
              >
                Refresh page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// API Configuration - centralized with runtime detection
import { BACKEND_URL as SMART_BACKEND_URL } from './utils';

const BACKEND_URL = SMART_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CDN_URL = process.env.REACT_APP_CDN_URL || '';

// Log API URL for debugging
if (typeof window !== 'undefined') {
  console.log('🔗 BLK PACKS API:', API);
  if (CDN_URL) console.log('🚀 CDN:', CDN_URL);
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  // === BULLETPROOF TOKEN STORAGE FOR WKWEBVIEW ===
  // WKWebView on iPad can lose localStorage under memory pressure.
  // CapacitorHttp can change response format.
  // We store token in THREE places and recover from any one.
  
  const saveToken = useCallback((tok) => {
    if (!tok || tok === 'undefined' || tok === 'null') return;
    try { localStorage.setItem("blk_token", tok); } catch(e) { console.warn("localStorage save failed:", e); }
    try { sessionStorage.setItem("blk_token", tok); } catch(e) {}
    try { document.cookie = `blk_token=${tok};path=/;max-age=${60*60*24*7};SameSite=Lax`; } catch(e) {}
  }, []);
  
  const getStoredToken = useCallback(() => {
    let tok = null;
    try { tok = localStorage.getItem("blk_token"); } catch(e) {}
    if (tok && tok !== 'undefined' && tok !== 'null') return tok;
    try { tok = sessionStorage.getItem("blk_token"); } catch(e) {}
    if (tok && tok !== 'undefined' && tok !== 'null') {
      // Restore to localStorage
      try { localStorage.setItem("blk_token", tok); } catch(e) {}
      return tok;
    }
    // Try cookie
    try {
      const match = document.cookie.match(/blk_token=([^;]+)/);
      if (match) {
        tok = match[1];
        if (tok && tok !== 'undefined' && tok !== 'null') {
          try { localStorage.setItem("blk_token", tok); } catch(e) {}
          return tok;
        }
      }
    } catch(e) {}
    return null;
  }, []);
  
  const clearAllTokens = useCallback(() => {
    try { localStorage.removeItem("blk_token"); } catch(e) {}
    try { sessionStorage.removeItem("blk_token"); } catch(e) {}
    try { document.cookie = "blk_token=;path=/;max-age=0"; } catch(e) {}
  }, []);

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    let t = null;
    try { t = localStorage.getItem("blk_token"); } catch(e) {}
    if (!t || t === 'undefined' || t === 'null') {
      try { t = sessionStorage.getItem("blk_token"); } catch(e) {}
    }
    if (!t || t === 'undefined' || t === 'null') {
      try {
        const m = document.cookie.match(/blk_token=([^;]+)/);
        if (m) t = m[1];
      } catch(e) {}
    }
    return (t && t !== 'undefined' && t !== 'null') ? t : null;
  });
  const [loading, setLoading] = useState(true);
  const isLoggingOut = useRef(false);

  // Stable logout helper
  const clearAuth = useCallback(() => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    clearAllTokens();
    setToken(null);
    setUser(null);
    setTimeout(() => { isLoggingOut.current = false; }, 100);
  }, [clearAllTokens]);

  // Fetch user data — server validates token, we NEVER clear auth client-side
  const fetchUser = useCallback(async (currentToken) => {
    const tokenToUse = currentToken || token || getStoredToken();
    if (!tokenToUse) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${tokenToUse}` },
        timeout: 10000
      });
      // Defensive: CapacitorHttp might return string body
      const userData = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
      setUser(userData);
      // Ensure token is in all storage
      saveToken(tokenToUse);
    } catch (err) {
      if (err.response?.status === 401) {
        const detail = (err.response?.data?.detail || "").toLowerCase();
        if (detail.includes("token expired")) {
          clearAuth();
        }
        // "Invalid token" — don't clear, might be a temporary CapacitorHttp glitch
      }
      // Network errors, timeouts, 5xx — KEEP user logged in
    }
    setLoading(false);
  }, [token, clearAuth, getStoredToken, saveToken]);

  // Initial auth check on mount
  useEffect(() => {
    fetchUser();
  }, []); // Run once on mount - fetchUser is stable

  // Initialize native bridge (Capacitor status bar, deep links, haptics)
  useEffect(() => {
    initNetworkWatcher(); // Start network monitoring for offline cache
    if (isNative()) {
      configureStatusBar();
      // NOTE: Biometric auto-login is OPT-IN now. The previous behavior
      // popped Face ID immediately on app launch which blocked the entire
      // UI from scrolling/tapping until the user authenticated — making
      // the app feel broken. Users now tap the "Sign in with Face ID"
      // button on the login screen if they want biometric sign-in.

      // Global haptic feedback on ALL interactive elements
      const handleGlobalTap = (e) => {
        const el = e.target.closest('button, a, [role="button"], [data-testid], input[type="submit"], input[type="checkbox"], .haptic-btn, [onclick]');
        if (el) {
          haptic.medium();
        }
      };
      document.addEventListener('pointerdown', handleGlobalTap, { passive: true });
      return () => document.removeEventListener('pointerdown', handleGlobalTap);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-validate when token changes (login/logout)
  useEffect(() => {
    if (token && !user && !loading) {
      fetchUser(token);
    }
  }, [token, user, loading, fetchUser]);

  // Restore session when user returns to tab/app — uses triple storage recovery
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const storedToken = getStoredToken();
        if (storedToken) {
          if (!token) setToken(storedToken);
          if (!user && !loading) fetchUser(storedToken);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, token, loading, fetchUser, getStoredToken]);

  // Axios interceptor — on auth failure, show a non-destructive "Re-login needed"
  // modal (via global flag + toast). DO NOT auto-clear auth — that yanks the
  // user out of whatever tab they were on and hides all content. The user
  // explicitly clicks the modal's "Log in again" button to re-auth.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(null); // null | {title, message, eta}

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // === Maintenance / Site-down detection ===
        // ONLY show the TIME OUT overlay when the backend EXPLICITLY
        // returns 503 with {maintenance:true,...}. We do NOT auto-trigger
        // on bare 502/504/network errors anymore — long-running flows
        // (pack rip mint, video transcode polling, AR uploads) routinely
        // hit transient failures that should be handled locally by the
        // component, not interrupt the whole app with a maintenance
        // screen. The site-status boot probe + 15s overlay poll handle
        // the genuine "admin flipped the switch" case.
        const status = error.response?.status;
        const data = error.response?.data;
        if (status === 503 && data?.maintenance) {
          setMaintenanceMode({
            title: data.title || "TIME OUT",
            message: data.message || "",
            eta: data.eta || null,
          });
        }

        if (error.response?.status === 401) {
          const detail = (error.response?.data?.detail || "").toLowerCase();
          // ONLY fire the modal on explicit "token expired" from the server.
          // Other 401s (invalid token, not authenticated, etc.) are handled
          // locally by whatever button/call produced them — no forced logout.
          if (detail.includes("token expired") && !isLoggingOut.current) {
            setSessionExpired(true);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // ── Global chunk-load error handler ─────────────────────────────
  // Some lazy-load failures escape the React ErrorBoundary (e.g. when
  // the import promise rejects asynchronously outside a render frame).
  // Catch those at the window level too and apply the same safe
  // one-shot reload pattern.
  useEffect(() => {
    const FLAG = "__blk_chunk_reload_attempted__";
    const isChunkErr = (err) => {
      const msg = (err && (err.message || err.reason?.message)) || String(err || "");
      const name = err?.name || err?.reason?.name || "";
      return (
        name === "ChunkLoadError" ||
        /Loading chunk [\d]+ failed/i.test(msg) ||
        /Loading CSS chunk/i.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg)
      );
    };
    const safeReload = () => {
      try {
        if (!sessionStorage.getItem(FLAG)) {
          sessionStorage.setItem(FLAG, String(Date.now()));
          window.location.reload();
        }
      } catch (e) {
        /* sessionStorage blocked */
      }
    };
    const onError = (event) => {
      if (isChunkErr(event.error || event)) safeReload();
    };
    const onRejection = (event) => {
      if (isChunkErr(event.reason || event)) safeReload();
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    // Clear the reload flag once we're successfully past the new build
    // (i.e. the app fully mounted), so a future genuine chunk error
    // later in the session can still trigger one reload.
    try {
      const t = setTimeout(() => {
        sessionStorage.removeItem(FLAG);
      }, 8000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("error", onError);
        window.removeEventListener("unhandledrejection", onRejection);
      };
    } catch (e) {
      return () => {
        window.removeEventListener("error", onError);
        window.removeEventListener("unhandledrejection", onRejection);
      };
    }
  }, []);

  // On boot — check maintenance status immediately so users loading the
  // site during a maintenance window see the overlay right away (instead
  // of waiting for some other API call to fail first).
  useEffect(() => {
    axios
      .get(`${API}/system/status`, { timeout: 6000 })
      .then((r) => {
        const m = r.data?.maintenance;
        if (m && m.enabled) {
          setMaintenanceMode({
            title: m.title || "TIME OUT",
            message: m.message || "",
            eta: m.eta || null,
          });
        }
      })
      .catch(() => {
        /* boot probe failure handled by other interceptors */
      });
  }, []);

  // Reset the session-expired flag whenever the user logs in again
  useEffect(() => {
    if (user && token) setSessionExpired(false);
  }, [user, token]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      
      // Defensive: CapacitorHttp might return string body instead of object
      const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
      
      if (!data.token) {
        throw new Error("No token in login response");
      }
      
      // Save token to ALL storage mechanisms
      saveToken(data.token);
      
      // Verify token was actually saved (WKWebView can silently fail)
      const verified = getStoredToken();
      if (!verified) {
        // Force save again
        try { localStorage.setItem("blk_token", data.token); } catch(e) {}
        try { sessionStorage.setItem("blk_token", data.token); } catch(e) {}
      }
      
      setToken(data.token);
      setUser(data.user);
      
      // Save credentials for Face ID / Touch ID on native
      saveCredentials(email, data.token);
      haptic.success();
      
      return data;
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      haptic.error();
      throw err;
    }
  };

  const register = async (email, password, username, referralCode = null, dateOfBirth = null, firstName = null, lastName = null) => {
    const payload = { email, password, username };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    if (dateOfBirth) {
      payload.date_of_birth = dateOfBirth;
    }
    // Recipient name on shipping label — collected at signup so the
    // admin doesn't have to chase users for it later. Backend stashes
    // these into `shipping_address.first_name` / `last_name`.
    if (firstName) {
      payload.first_name = firstName;
    }
    if (lastName) {
      payload.last_name = lastName;
    }
    try {
      console.log('Register attempt to:', `${API}/auth/register`);
      const res = await axios.post(`${API}/auth/register`, payload);
      return res.data;
    } catch (err) {
      console.error('Register error:', err.response?.data || err.message);
      throw err;
    }
  };

  const logout = () => {
    clearAuth();
  };

  const refreshUser = () => fetchUser(token);

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser, sessionExpired, setSessionExpired }}>
      {children}
      {maintenanceMode && (
        <MaintenanceOverlay
          initial={maintenanceMode}
          onDismiss={() => setMaintenanceMode(null)}
        />
      )}
      <StickyGetAppBar />
    </AuthContext.Provider>
  );
};

// API Helper
const authApi = (token) => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${token}` }
});

// ============== VALUE CALCULATION SYSTEM ==============
// Calculates total collectible value based on Metal + Rarity + Serial Number

const RARITY_MULTIPLIERS = {
  'COMMON': 1.0,
  'RARE': 1.5,
  'EPIC': 2.0,
  'LEGENDARY': 3.0  // Already gold-backed, gets highest multiplier
};

const RARITY_BONUS = {
  'COMMON': 0,
  'RARE': 2.00,
  'EPIC': 5.00,
  'LEGENDARY': 15.00
};

// Serial number bonus calculation
// Lower serials = higher value, with #1 being the most valuable
const calculateSerialBonus = (serialNumber, totalEditions) => {
  if (!serialNumber || !totalEditions || totalEditions <= 0) return 0;
  
  const MAX_SERIAL_BONUS = 50.00; // Maximum bonus for #1
  
  // Special bonuses for ultra-rare serials
  if (serialNumber === 1) return MAX_SERIAL_BONUS; // #1 is the holy grail
  if (serialNumber <= 10) return MAX_SERIAL_BONUS * 0.6 * (1 - (serialNumber - 1) / 10); // Top 10
  if (serialNumber <= 100) return MAX_SERIAL_BONUS * 0.2 * (1 - (serialNumber - 10) / 90); // Top 100
  
  // For regular serials, use a curve based on position
  // Lower percentage of total = higher bonus
  const position = serialNumber / totalEditions;
  if (position <= 0.01) return 10.00; // Top 1%
  if (position <= 0.05) return 5.00;  // Top 5%
  if (position <= 0.10) return 2.50;  // Top 10%
  if (position <= 0.25) return 1.00;  // Top 25%
  
  return 0; // Bottom 75% gets no serial bonus
};

// Get serial tier label
const getSerialTier = (serialNumber, totalEditions) => {
  if (!serialNumber || !totalEditions) return null;
  if (serialNumber === 1) return { tier: 'GENESIS', color: 'text-amber-300', bg: 'bg-orange-500/20' };
  if (serialNumber <= 10) return { tier: 'TOP 10', color: 'text-amber-400', bg: 'bg-amber-500/20' };
  if (serialNumber <= 100) return { tier: 'TOP 100', color: 'text-blue-400', bg: 'bg-blue-500/20' };
  const position = serialNumber / totalEditions;
  if (position <= 0.01) return { tier: 'TOP 1%', color: 'text-green-400', bg: 'bg-green-500/20' };
  if (position <= 0.05) return { tier: 'TOP 5%', color: 'text-teal-400', bg: 'bg-teal-500/20' };
  if (position <= 0.10) return { tier: 'TOP 10%', color: 'text-amber-400', bg: 'bg-amber-500/20' };
  return null;
};

// Calculate total collectible value
const calculateTotalValue = (collectible, metalPrices) => {
  if (!collectible) return { total: 0, breakdown: {} };

  // HOBBY DROPS — licensing-protected physical card hunts. NEVER assign
  // metal/rarity/serial dollar values; surfacing any $ would imply we're
  // valuing the licensed manufacturer/league IP.
  if (collectible.is_hobby || collectible.is_hobby_drop) {
    return {
      total: 0,
      breakdown: {
        metalValue: 0,
        adjustedMetalValue: 0,
        rarityMultiplier: 1,
        rarityBonus: 0,
        serialBonus: 0,
        sealedBonus: 0,
        isSealed: !!collectible.is_sealed,
        serialTier: null,
        metalType: null,
        isHobby: true,
      },
    };
  }

  const rarity = collectible.rarity?.toUpperCase() || 'COMMON';
  const isSealed = collectible.is_sealed;
  
  // 1. Base Metal Value (from live prices)
  const baseMetalValue = rarity === 'LEGENDARY' 
    ? (metalPrices?.gold_blkback_price || 9.00)
    : (metalPrices?.silver_blkback_price || 6.00);
  
  // 2. Rarity Bonus
  const rarityBonus = RARITY_BONUS[rarity] || 0;
  
  // 3. Serial Number Bonus (only if pack is opened/unsealed)
  const serialBonus = isSealed ? 0 : calculateSerialBonus(
    collectible.edition_number, 
    collectible.total_editions
  );
  
  // 4. Rarity Multiplier applied to base metal value
  const rarityMultiplier = RARITY_MULTIPLIERS[rarity] || 1.0;
  const adjustedMetalValue = baseMetalValue * rarityMultiplier;
  
  // 5. Sealed Pack Bonus - unopened packs have mystery value premium
  const sealedBonus = isSealed ? (adjustedMetalValue * 0.15) : 0; // 15% premium for sealed packs
  
  // Total = Adjusted Metal + Rarity Bonus + Serial Bonus + Sealed Bonus
  const total = adjustedMetalValue + rarityBonus + serialBonus + sealedBonus;
  
  return {
    total,
    breakdown: {
      metalValue: baseMetalValue,
      adjustedMetalValue,
      rarityMultiplier,
      rarityBonus,
      serialBonus,
      sealedBonus,
      isSealed,
      serialTier: isSealed ? null : getSerialTier(collectible.edition_number, collectible.total_editions),
      metalType: rarity === 'LEGENDARY' ? 'Gold' : 'Silver'
    }
  };
};

// Helper to check if a collectible is gift-locked (30-day fraud prevention)
const getGiftLockStatus = (collectible) => {
  if (!collectible?.gift_locked_until) return { isLocked: false };
  
  const lockTime = new Date(collectible.gift_locked_until);
  const now = new Date();
  
  if (now >= lockTime) return { isLocked: false };
  
  const diffMs = lockTime - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  let timeRemaining;
  if (diffDays > 0) {
    timeRemaining = `${diffDays}d ${diffHours}h`;
  } else if (diffHours > 0) {
    timeRemaining = `${diffHours}h ${diffMins}m`;
  } else {
    timeRemaining = `${diffMins}m`;
  }
  
  return {
    isLocked: true,
    timeRemaining,
    unlockDate: lockTime.toLocaleDateString(),
    daysRemaining: diffDays,
    hoursRemaining: diffHours
  };
};

// ============== CERTIFICATE COMPONENT ==============
// Digital authenticity certificate for collectibles from drops and challenges
// Premium design with animations, shimmer effects, and luxury styling
const CollectibleCertificate = ({ collectible, onClose }) => {
  if (!collectible) return null;
  
  const isChallenge = collectible.source === 'challenge';
  const certificateType = isChallenge ? 'CHALLENGE REWARD' : 'PACK DROP';
  const acquiredDate = collectible.acquired_at || collectible.created_at || new Date().toISOString();
  const formattedDate = new Date(acquiredDate).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  return (
    <ModalPortal>
    <div className="fixed inset-0 bg-[#12121e]/98 backdrop-blur-md z-50 flex items-center justify-center p-4">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-orange-500/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>
      
      <div className="relative max-w-2xl w-full animate-fade-in">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute -top-3 -right-3 z-20 w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 rounded-full flex items-center justify-center text-white border border-orange-500/30 shadow-lg shadow-black/50 transition-all hover:scale-110"
        >
          <X size={20} />
        </button>
        
        {/* Certificate Container with glow */}
        <div className="relative">
          {/* Outer glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-orange-500/20 via-amber-500/10 to-orange-500/20 rounded-2xl blur-xl"></div>
          
          {/* Certificate */}
          <div className="relative bg-gradient-to-b from-[#0a0a0c] via-[#050507] to-[#0a0a0c] rounded-xl overflow-hidden border border-orange-500/30">
            
            {/* Animated shimmer overlay */}
            <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden">
              <div 
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(105deg, transparent 40%, rgba(249,115,22,0.4) 45%, rgba(249,115,22,0.8) 50%, rgba(249,115,22,0.4) 55%, transparent 60%)',
                  animation: 'shimmer 3s ease-in-out infinite',
                  backgroundSize: '200% 100%'
                }}
              />
            </div>
            
            {/* Sparkle overlay */}
            <div className="absolute inset-0 opacity-40" style={{
              backgroundImage: `
                radial-gradient(circle at 15% 25%, #FBBF24 1px, transparent 1px),
                radial-gradient(circle at 85% 15%, #FBBF24 0.5px, transparent 0.5px),
                radial-gradient(circle at 45% 75%, #FBBF24 1px, transparent 1px),
                radial-gradient(circle at 75% 85%, #FBBF24 0.5px, transparent 0.5px),
                radial-gradient(circle at 25% 60%, #FBBF24 0.5px, transparent 0.5px),
                radial-gradient(circle at 90% 50%, #FBBF24 1px, transparent 1px)
              `,
              backgroundSize: '150px 150px, 100px 100px, 200px 200px, 80px 80px, 120px 120px, 180px 180px'
            }}></div>
            
            {/* Premium Triple Border */}
            <div className="absolute inset-3 border-2 border-orange-500/60 rounded-lg pointer-events-none">
              <div className="absolute inset-2 border border-orange-500/30 rounded-lg"></div>
              <div className="absolute inset-4 border border-orange-500/15 rounded-lg"></div>
              
              {/* Animated corner ornaments */}
              {[
                'top-0 left-0 border-t-2 border-l-2 rounded-tl-lg',
                'top-0 right-0 border-t-2 border-r-2 rounded-tr-lg',
                'bottom-0 left-0 border-b-2 border-l-2 rounded-bl-lg',
                'bottom-0 right-0 border-b-2 border-r-2 rounded-br-lg'
              ].map((pos, i) => (
                <div key={i} className={`absolute w-12 h-12 ${pos} border-orange-500/80`}>
                  <div className="absolute inset-1 w-2 h-2 bg-orange-500/50 rounded-full" 
                       style={{animation: `pulse 2s ease-in-out infinite`, animationDelay: `${i * 0.3}s`}}></div>
                </div>
              ))}
            </div>
            
            {/* Content */}
            <div className="relative p-8 md:p-12 text-center">
              
              {/* Top decorative line with glow */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-orange-500/60 to-transparent"></div>
                <div className="relative">
                  <Star className="text-amber-400 animate-pulse" size={14} fill="currentColor" />
                  <div className="absolute inset-0 text-amber-400 blur-sm pointer-events-none"><Star size={14} fill="currentColor" /></div>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-orange-500/60 to-transparent"></div>
              </div>
              
              {/* BLK Logo with glow */}
              <div className="mb-4 relative inline-block">
                <span className="text-4xl md:text-5xl font-black tracking-tight">
                  <span className="text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">BLK</span>
                  <span className="text-amber-400 drop-shadow-[0_0_15px_rgba(249,115,22,0.6)]">.</span>
                </span>
              </div>
              
              {/* Certificate Type with diamond accents */}
              <div className="mb-4">
                <h1 className="text-xl md:text-2xl font-black tracking-[0.2em] relative inline-block">
                  <span className="text-amber-400/60 mr-2">◈</span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-orange-500 to-amber-400 drop-shadow-[0_0_20px_rgba(249,115,22,0.4)]">
                    {certificateType} CERTIFICATE
                  </span>
                  <span className="text-amber-400/60 ml-2">◈</span>
                </h1>
              </div>
              
              {/* Glowing divider line */}
              <div className="relative mb-6 mx-auto" style={{width: '280px'}}>
                <div className="h-0.5 bg-gradient-to-r from-transparent via-orange-500 to-transparent"></div>
                <div className="absolute inset-0 h-0.5 bg-gradient-to-r from-transparent via-orange-500 to-transparent blur-sm pointer-events-none"></div>
              </div>
              
              {/* Authenticates Text */}
              <p className="text-gray-500 text-xs tracking-[0.25em] mb-2 uppercase">This Certificate Authenticates</p>
              
              {/* Main Statement */}
              <h2 className="text-base md:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-200 via-amber-400 to-orange-200 mb-1 tracking-wide">
                YOUR EXCLUSIVE COLLECTED MOMENT
              </h2>
              <h3 className="text-sm md:text-base font-semibold text-transparent bg-clip-text bg-gradient-to-r from-amber-400/80 to-amber-400/80 mb-1">
                & PHYSICAL METAL NOTE
              </h3>
              <p className="text-gray-600 text-xs tracking-[0.2em] mb-8">IN THE BLK. ECOSYSTEM</p>
              
              {/* Seal and Details Row */}
              <div className="flex items-center gap-6 mb-8">
                
                {/* Premium Animated Seal */}
                <div className="relative w-28 h-28 md:w-32 md:h-32 flex-shrink-0">
                  {/* Outer glow ring */}
                  <div className="absolute inset-0 rounded-full" style={{
                    background: 'radial-gradient(circle, rgba(249,115,22,0.15) 0%, transparent 70%)',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}></div>
                  
                  {/* Rotating outer ring */}
                  <div className="absolute inset-0 rounded-full border-4 border-orange-500/40"
                       style={{animation: 'spin 20s linear infinite'}}></div>
                  
                  {/* Main seal border */}
                  <div className="absolute inset-2 rounded-full border-2 border-orange-500/70 shadow-[0_0_15px_rgba(249,115,22,0.3)]"></div>
                  
                  {/* Dashed inner ring */}
                  <div className="absolute inset-4 rounded-full border border-dashed border-orange-500/40"></div>
                  
                  {/* Seal content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-amber-400 text-[8px] md:text-[9px] font-bold tracking-wider">{certificateType}</span>
                    <span className="text-white font-black text-xl md:text-2xl my-0.5 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">BLK.</span>
                    <span className="text-amber-400/70 text-[6px] md:text-[7px] tracking-wider">OFFICIAL</span>
                    <span className="text-amber-400/70 text-[6px] md:text-[7px] tracking-wider">AUTHENTICITY</span>
                  </div>
                  
                  {/* Cardinal stars with glow */}
                  {['top-0 left-1/2 -translate-x-1/2', 'bottom-0 left-1/2 -translate-x-1/2', 
                    'left-0 top-1/2 -translate-y-1/2', 'right-0 top-1/2 -translate-y-1/2'].map((pos, i) => (
                    <div key={i} className={`absolute ${pos} text-amber-400 text-sm`}>
                      <span className="drop-shadow-[0_0_5px_rgba(249,115,22,0.8)]">★</span>
                    </div>
                  ))}
                </div>
                
                {/* Collectible Details Panel */}
                <div className="flex-1 text-left">
                  <div className="relative bg-[#12121e]/70 rounded-xl p-4 md:p-5 overflow-hidden">
                    {/* Panel glow border */}
                    <div className="absolute inset-0 rounded-xl border border-orange-500/30"></div>
                    <div className="absolute inset-px rounded-xl border border-orange-500/10"></div>
                    
                    {/* Panel header */}
                    <div className="flex items-center gap-2 mb-3">
                      <p className="text-gray-500 text-xs tracking-wider">COLLECTIBLE</p>
                      <div className="flex-1 h-px bg-gradient-to-r from-orange-500/30 to-transparent"></div>
                    </div>
                    
                    <p className="text-white font-bold text-sm md:text-base mb-4 truncate">{collectible.name}</p>
                    
                    {/* Details grid */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {/* Serial - Featured */}
                      <div className="relative col-span-1">
                        <p className="text-gray-500 text-[10px] tracking-wider mb-1">SERIAL NUMBER</p>
                        <div className="relative inline-block">
                          <p className="text-amber-400 font-black text-2xl md:text-3xl drop-shadow-[0_0_10px_rgba(249,115,22,0.4)]">
                            #{collectible.edition_number}
                          </p>
                          <div className="absolute -inset-1 bg-orange-500/10 rounded blur-md -z-10"></div>
                        </div>
                      </div>
                      
                      {/* Edition */}
                      <div>
                        <p className="text-gray-500 text-[10px] tracking-wider mb-1">EDITION</p>
                        <p className="text-gray-300 font-medium text-sm">of {collectible.total_editions}</p>
                      </div>
                      
                      {/* Rarity */}
                      <div>
                        <p className="text-gray-500 text-[10px] tracking-wider mb-1">RARITY</p>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                          collectible.rarity === 'LEGENDARY' ? 'bg-orange-500/20 text-amber-300 shadow-[0_0_10px_rgba(249,115,22,0.3)]' :
                          collectible.rarity === 'EPIC' ? 'bg-amber-500/20 text-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.3)]' :
                          collectible.rarity === 'RARE' ? 'bg-blue-500/20 text-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.3)]' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>{collectible.rarity || 'COMMON'}</span>
                      </div>
                      
                      {/* Metal */}
                      <div>
                        <p className="text-gray-500 text-[10px] tracking-wider mb-1">METAL TYPE</p>
                        <p className={`font-bold text-sm ${collectible.metal_type === 'gold' ? 'text-amber-300' : 'text-gray-300'}`}>
                          {(collectible.metal_type || 'Silver').toUpperCase()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bottom decorative line */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-20 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent"></div>
                <span className="text-amber-400/60 text-sm">◆</span>
                <div className="w-20 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent"></div>
              </div>
              
              {/* Footer - Date and Authenticated By */}
              <div className="flex justify-between items-end px-2">
                <div className="text-left">
                  <p className="text-gray-600 text-[10px] uppercase tracking-wider mb-1">Acquisition Date</p>
                  <p className="text-gray-400 text-sm font-semibold">{formattedDate}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-600 text-[10px] uppercase tracking-wider mb-1">Authenticated By</p>
                  <div className="font-black text-xl">
                    <span className="text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.2)]">BLK</span>
                    <span className="text-amber-400 drop-shadow-[0_0_10px_rgba(249,115,22,0.5)]">PACKS</span>
                  </div>
                </div>
              </div>
              
              {/* Certificate ID */}
              <p className="text-gray-700 text-[9px] mt-4 tracking-wider">
                CERTIFICATE ID: {collectible.id?.substring(0, 24).toUpperCase()}
              </p>

              {/* EMERGE Verification ID + Public Link */}
              {collectible.verification_id && (
                <div className="mt-5 border border-emerald-500/25 bg-emerald-500/[0.04] rounded-xl p-3 text-left" data-testid="app-cert-emerge-block">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400/80 mb-1">EMERGE Verification ID</div>
                  <div className="text-emerald-300 font-mono text-xs break-all mb-2">{collectible.verification_id}</div>
                  <Link
                    to={`/verify/${encodeURIComponent(collectible.verification_id)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-emerald-300 hover:text-emerald-200"
                    data-testid="app-cert-emerge-link"
                  >
                    View on EMERGE ↗
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Download PDF Button with glow */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <a
            href={`${API}/locker/certificate/${collectible.id}`}
            download
            className="group relative flex items-center gap-2 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 hover:from-amber-400 hover:via-amber-400 hover:to-amber-400 text-white font-bold px-8 py-3.5 rounded-xl transition-all duration-300 shadow-lg shadow-orange-500/40 hover:shadow-orange-500/60 hover:scale-105"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-400 rounded-xl blur opacity-50 group-hover:opacity-75 transition-opacity pointer-events-none"></div>
            <Download size={18} className="relative z-10" />
            <span className="relative z-10">Download PDF Certificate</span>
          </a>
        </div>
        
        {/* Hint text */}
        <p className="text-center text-gray-600 text-xs mt-4">
          Save your official BLK. certificate matching Serial <span className="text-amber-400/70 font-semibold">#{collectible.edition_number}</span>
        </p>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.3; }
          50% { transform: translateY(-20px) scale(1.2); opacity: 0.6; }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
    </ModalPortal>
  );
};

// ============== COMPONENTS ==============

// Navbar wrapper that uses useAuth hook and passes props to the extracted Navbar component// MaintenanceGate — reads useAuth so admins bypass the TIME OUT screen.
const MaintenanceGate = ({ children }) => {
  const { user } = useAuth();
  return <MaintenanceGuard isAdmin={!!user?.is_admin}>{children}</MaintenanceGuard>;
};

import NavbarComponent from "./components/layout/Navbar";

const NavbarWrapper = () => {
  const { user, logout, token } = useAuth();
  return <NavbarComponent user={user} logout={logout} token={token} />;
};

// MobileBottomNav wrapper that uses useAuth hook
const MobileBottomNavWrapper = () => {
  const { user, token } = useAuth();
  return <MobileBottomNav user={user} token={token} />;
};

// ChallengesPage wrapper that passes auth context
const ChallengesPageWrapper = () => {
  const auth = useAuth();
  return <ChallengesPageComponent authContext={auth} />;
};

// RunIt 1V1 page wrapper
const RunItPageWrapper = () => {
  const auth = useAuth();
  return <RunItPageComponent authContext={auth} />;
};

// Tournaments page wrapper
const TournamentsPageWrapper = () => {
  const auth = useAuth();
  return <TournamentsPageComponent authContext={auth} />;
};

const StakingPageWrapper = () => {
  const auth = useAuth();
  return <StakingPageComponent {...auth} />;
};

const MySetsPageWrapper = () => {
  const auth = useAuth();
  return <MySetsPageComponent authContext={auth} />;
};

const SeriesPageWrapper = () => {
  const { token } = useAuth();
  return <SeriesPageComponent token={token} />;
};

const FirstRipPageWrapper = () => {
  const auth = useAuth();
  return <FirstRipPageComponent authContext={auth} />;
};

// Wrappers for extracted collectible components (use local versions until fully migrated)
const CollectibleCard = CollectibleCardComponent;
const RotatingCube = RotatingCubeComponent;

// Use extracted AdminChallengesTab
const AdminChallengesTab = AdminChallengesTabComponent;

// Page wrappers for extracted pages
const PlayPageWrapper = () => {
  const auth = useAuth();
  return (
    <>
      <SEOHead
        title="Drops — New Trading Card Releases"
        description="Daily card pack drops on BLK PACKS. Rip live, hunt rookies, autographs and refractors, and chase Monster pulls. New drops every week."
        path="/drops"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "BLK PACKS Drops",
          "description": "Latest trading card pack drops with live ripping and instant ownership.",
        }}
      />
      <PlayPageComponent authContext={auth} />
    </>
  );
};

const SellPageWrapper = () => {
  const auth = useAuth();
  return (
    <>
      <SEOHead
        title="Sell Your Cards Instantly — BLK PACKS Buyback"
        description="Sell your trading cards in minutes. Get an instant tiered cash offer or +10% in BLK Credit. Make us a counter-offer — we negotiate in chat within 24h."
        path="/sell"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "How fast do I get paid?", "acceptedAnswer": { "@type": "Answer", "text": "Most accepted offers are paid within 24 hours of receipt via Cash App, Zelle, USDC, BTC, or BLK Credit." } },
            { "@type": "Question", "name": "Do you take graded and raw cards?", "acceptedAnswer": { "@type": "Answer", "text": "Yes — PSA, BGS, SGC slabs and raw cards. Tier-1 names (Wemby, Mahomes, Jordan, Kobe, LeBron) get 80–90% of market." } },
            { "@type": "Question", "name": "Can I counter-offer?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Hit \"Make Us An Offer\" on any quote and our buyer will message you in chat within 24 hours." } },
          ],
        }}
      />
      <SellPageComponent authContext={auth} />
    </>
  );
};

const MarketplacePageWrapper = () => {
  const auth = useAuth();
  return (
    <>
      <SEOHead
        title="Marketplace — Buy & Sell Trading Cards"
        description="Browse the BLK PACKS marketplace for graded rookies, autos, refractors and Monster pulls — verified by AI, shipped fast."
        path="/marketplace"
      />
      <MarketplacePageComponent authContext={auth} />
    </>
  );
};

const LiveBreaksPageWrapper = () => {
  const auth = useAuth();
  return (
    <>
      <SEOHead
        title="Live Card Breaks — Watch & Rip Real-Time"
        description="Live card breaks streaming now on BLK PACKS. Join a slot, watch the rip live, and own every card you pulled — instantly added to your locker."
        path="/live"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Live Card Breaks",
          "description": "Real-time card break streaming with instant pack ownership.",
        }}
      />
      <LiveBreaksPageComponent authContext={auth} />
    </>
  );
};

const LiveBreakRoomPageWrapper = () => {
  const auth = useAuth();
  return <LiveBreakRoomPageComponent authContext={auth} />;
};

const RipPageWrapper = () => {
  const auth = useAuth();
  return <RipPageComponent authContext={auth} />;
};

// AuthLoadingGate - prevents signed-out flash on refresh for logged-in users
// Only blocks rendering when a token exists but user hasn't loaded yet
const AuthLoadingGate = ({ children }) => {
  const { loading, token } = useAuth();

  // Only intercept rendering if there's a token being validated (returning
  // user). New visitors (no token) see the page immediately.
  //
  // VISUAL: pure black scrim with no logo, no "BLK PACKS" text, no spinner.
  // The previous version flashed a centered "BLK PACKS" wordmark + amber
  // spinner on every page navigation — the user explicitly killed it.
  // Keeping the scrim alive (instead of returning `null`) so we still
  // block the signed-out UI flicker, but the scrim is now indistinguishable
  // from the page bg → no perceived splash.
  if (loading && token) {
    return <div className="min-h-screen bg-black" aria-hidden="true" />;
  }

  return children;
};

// HomePageWrapper - renders HomePage with auth context
const HomePageWrapper = () => {
  const auth = useAuth();
  return (
    <>
      <SEOHead
        title="Digital + Physical Trading Cards, AR + Live Breaks"
        description="BLK PACKS: rip digital + physical sports trading card packs, watch live breaks, scan cards with AI for instant value, and sell back any card for cash or BLK Credit."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "BLK PACKS",
          "url": "https://www.blkpacks.fun",
          "logo": "https://www.blkpacks.fun/images/blk-gradient-b-transparent.png",
          "sameAs": ["https://twitter.com/blkpacks", "https://www.instagram.com/blkpacks"],
        }}
      />
      <HomePageComponent authContext={auth || {}} />
    </>
  );
};

const LockerPageWrapper = () => {
  const auth = useAuth();
  return <LockerPageComponent authContext={auth} />;
};

const BoostCheckoutPageWrapper = () => {
  const auth = useAuth();
  return <BoostCheckoutPageComponent authContext={auth} />;
};

const WalletPageWrapper = () => {
  const auth = useAuth();
  return <WalletPageComponent authContext={auth} />;
};

const AdminPageWrapper = () => {
  const auth = useAuth();
  return <AdminPageComponent authContext={auth} />;
};

const ProfilePageWrapper = () => {
  const auth = useAuth();
  return <ProfilePageComponent authContext={auth} />;
};

const ActivityFeedPageWrapper = () => {
  return <ActivityFeedPageComponent />;
};

const PublicProfilePageWrapper = () => {
  return <PublicProfilePageComponent />;
};

const EventsPageWrapper = () => {
  const auth = useAuth();
  return <EventsPageComponent authContext={auth} />;
};

const LoadingSpinner = ({ text = "Loading..." }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
    <p className="mt-4 text-gray-400">{text}</p>
  </div>
);

const RarityBadge = ({ rarity }) => {
  const colors = {
    COMMON: "bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 border border-gray-500/50",
    RARE: "bg-gradient-to-r from-blue-700 to-blue-900 text-blue-100 border border-blue-400/50",
    EPIC: "bg-gradient-to-r from-amber-700 to-amber-900 text-amber-100 border border-amber-400/50",
    LEGENDARY: "bg-gradient-to-r from-orange-600 to-orange-700 text-white border border-amber-400/50",
  };
  return (
    <span className={`px-2.5 py-1 rounded text-xs font-black uppercase tracking-wide ${colors[rarity] || colors.COMMON}`}>
      {rarity}
    </span>
  );
};

// Drop Rarity Badge - For categorizing entire drops (no emojis, premium dark style)
const DropRarityBadge = ({ rarity, size = "sm" }) => {
  const rarityLower = (rarity || "common").toLowerCase();
  const config = {
    common: { 
      bg: "bg-gradient-to-r from-gray-700 to-gray-800", 
      border: "border-gray-500/50",
      text: "text-gray-300"
    },
    rare: { 
      bg: "bg-gradient-to-r from-blue-700 to-blue-900", 
      border: "border-blue-400/50",
      text: "text-blue-100"
    },
    epic: { 
      bg: "bg-gradient-to-r from-amber-700 to-amber-900", 
      border: "border-amber-400/50",
      text: "text-amber-100"
    },
    legendary: { 
      bg: "bg-gradient-to-r from-orange-600 to-orange-700", 
      border: "border-amber-400/50",
      text: "text-white"
    }
  };
  const c = config[rarityLower] || config.common;
  const sizeClasses = {
    xs: "px-2 py-0.5 text-[10px]",
    sm: "px-2.5 py-1 text-xs",
    md: "px-3 py-1.5 text-sm",
    lg: "px-4 py-2 text-base"
  };
  return (
    <span className={`inline-flex items-center rounded font-black uppercase tracking-wide border ${c.bg} ${c.border} ${c.text} ${sizeClasses[size]}`}>
      {rarityLower.toUpperCase()}
    </span>
  );
};

// Verified Badge - Like X/Twitter blue check but green for trust
const VerifiedBadge = ({ verified, verifiedAt, showDate = false, size = "sm" }) => {
  if (!verified) return null;
  
  const sizeClasses = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };
  
  return (
    <span className="inline-flex items-center gap-1" title={`Verified ${verifiedAt ? new Date(verifiedAt).toLocaleDateString() : ''}`}>
      <span className={`${sizeClasses[size]} bg-green-500 rounded-full flex items-center justify-center`}>
        <Check className="text-white" size={size === "xs" ? 8 : size === "sm" ? 10 : size === "md" ? 12 : 14} strokeWidth={3} />
      </span>
      {showDate && verifiedAt && (
        <span className="text-green-500 text-xs">Verified {new Date(verifiedAt).toLocaleDateString()}</span>
      )}
    </span>
  );
};

// X (Twitter) Icon
const XIcon = ({ size = 20, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// TikTok Icon
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Footer is now imported from ./components/layout/Footer


// CollectibleCard extracted to /components/collectibles/CollectibleCard.jsx

// CollectibleCube - Interactive 3D cube for marketplace collectibles with video
const CollectibleCube = ({ collectible, onPopOut, size = "md" }) => {
  const [rotation, setRotation] = useState({ x: -15, y: 0 });
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const cubeRef = useRef(null);
  const animationRef = useRef(null);

  // Cube sizing based on size prop
  const cubeSize = size === "sm" ? 100 : size === "lg" ? 160 : 130;
  const translateZ = cubeSize / 2;

  // Idle spin when not dragging
  useEffect(() => {
    if (isDragging) return;
    const spinInterval = setInterval(() => {
      setRotation(prev => ({ x: prev.x, y: prev.y + 0.3 }));
    }, 50);
    return () => clearInterval(spinInterval);
  }, [isDragging]);

  // Momentum animation
  useEffect(() => {
    if (isDragging) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }
    const animate = () => {
      setVelocity(prev => {
        const newVelX = prev.x * 0.95;
        const newVelY = prev.y * 0.95;
        if (Math.abs(newVelX) < 0.1 && Math.abs(newVelY) < 0.1) return { x: 0, y: 0 };
        setRotation(r => ({ x: Math.max(-60, Math.min(60, r.x + newVelX)), y: r.y + newVelY }));
        return { x: newVelX, y: newVelY };
      });
      animationRef.current = requestAnimationFrame(animate);
    };
    if (Math.abs(velocity.x) > 0.1 || Math.abs(velocity.y) > 0.1) {
      animationRef.current = requestAnimationFrame(animate);
    }
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [isDragging, velocity]);

  const handleStart = (clientX, clientY) => { setIsDragging(true); setLastPos({ x: clientX, y: clientY }); setVelocity({ x: 0, y: 0 }); };
  const handleMove = (clientX, clientY) => {
    if (!isDragging) return;
    const deltaX = clientX - lastPos.x;
    const deltaY = clientY - lastPos.y;
    setRotation(prev => ({ x: Math.max(-60, Math.min(60, prev.x - deltaY * 0.5)), y: prev.y + deltaX * 0.5 }));
    setVelocity({ x: -deltaY * 0.3, y: deltaX * 0.3 });
    setLastPos({ x: clientX, y: clientY });
  };
  const handleEnd = () => setIsDragging(false);

  const onMouseDown = (e) => handleStart(e.clientX, e.clientY);
  const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
  const onMouseUp = () => handleEnd();
  const onMouseLeave = () => { if (isDragging) handleEnd(); };
  const onTouchStart = (e) => { const t = e.touches[0]; handleStart(t.clientX, t.clientY); };
  const onTouchMove = (e) => { const t = e.touches[0]; handleMove(t.clientX, t.clientY); };
  const onTouchEnd = () => handleEnd();

  const handleClick = () => {
    if (!isDragging) {
      // Always use the enhanced PopOut modal
      if (onPopOut) {
        onPopOut(collectible);
      } else if (collectible.video_url) {
        setShowVideoPopup(true);
      }
    }
  };

  return (
    <>
      <div 
        ref={cubeRef}
        className={`collectible-cube-wrapper cursor-grab ${isDragging ? 'cursor-grabbing' : ''}`}
        style={{ width: cubeSize + 20, height: cubeSize + 30, userSelect: 'none' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={handleClick}
        data-testid={`collectible-cube-${collectible.id}`}
      >
        <div className="collectible-cube-container" style={{ width: cubeSize, height: cubeSize, perspective: cubeSize * 3, margin: '0 auto' }}>
          <div 
            className="collectible-cube"
            style={{ 
              width: cubeSize, height: cubeSize,
              transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              transformStyle: 'preserve-3d', position: 'relative'
            }}
          >
            {/* Top - Black with Yellow BLK text (matches bottom PACKS style) */}
            <div style={{ 
              position: 'absolute', width: cubeSize, height: cubeSize,
              transform: `rotateX(90deg) translateZ(${translateZ}px)`,
              background: '#12121e',
              border: '2px solid #FBBF24',
              borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              backfaceVisibility: 'hidden'
            }}>
              <span style={{ 
                color: '#FBBF24', 
                fontWeight: 900, 
                fontSize: cubeSize * 0.3, 
                letterSpacing: '0.05em'
              }}>BLK</span>
            </div>
            
            {/* Front - Image with Video badge */}
            <div style={{ 
              position: 'absolute', width: cubeSize, height: cubeSize,
              transform: `translateZ(${translateZ}px)`,
              background: '#12121e', border: '2px solid rgba(249,115,22,0.5)',
              borderRadius: '8px', overflow: 'hidden', backfaceVisibility: 'hidden'
            }}>
              <img src={getMediaUrl(collectible.image_url)} alt={collectible.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {collectible.video_url && (
                <div style={{ position: 'absolute', top: 4, left: 4, background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, background: '#fff', borderRadius: '50%', animation: 'pulse 1s infinite' }}></span> MOMENT
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, #000, transparent)', padding: 6 }}>
                <span style={{ color: '#F97316', fontWeight: 700, fontSize: 11 }}>#{collectible.edition_number}</span>
              </div>
            </div>
            
            {/* Back - Serial */}
            <div style={{ 
              position: 'absolute', width: cubeSize, height: cubeSize,
              transform: `rotateY(180deg) translateZ(${translateZ}px)`,
              background: '#12121e', border: '2px solid rgba(249,115,22,0.3)',
              borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backfaceVisibility: 'hidden'
            }}>
              <span style={{ color: '#6b7280', fontSize: 9 }}>SERIAL</span>
              <span style={{ color: '#F97316', fontWeight: 900, fontSize: cubeSize * 0.25 }}>#{collectible.edition_number}</span>
              <span style={{ color: '#4b5563', fontSize: 9 }}>of {collectible.total_editions}</span>
            </div>
            
            {/* Right - Video Preview or Rarity */}
            <div style={{ 
              position: 'absolute', width: cubeSize, height: cubeSize,
              transform: `rotateY(90deg) translateZ(${translateZ}px)`,
              background: '#12121e', border: '2px solid rgba(249,115,22,0.3)',
              borderRadius: '8px', overflow: 'hidden', backfaceVisibility: 'hidden'
            }}>
              {collectible.video_url ? (
                <>
                  <video autoPlay muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }}>
                    <source src={getMediaUrl(collectible.video_url)} type="video/mp4" />
                  </video>
                  <div style={{ position: 'absolute', top: 4, left: 4, background: '#dc2626', color: '#fff', fontSize: 8, fontWeight: 700, padding: '2px 4px', borderRadius: 4 }}>
                    <span style={{ width: 5, height: 5, background: '#fff', borderRadius: '50%', display: 'inline-block', marginRight: 3, animation: 'pulse 1s infinite' }}></span>LIVE
                  </div>
                </>
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Star style={{ color: '#F97316', marginBottom: 4 }} size={cubeSize * 0.15} />
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>{collectible.rarity}</span>
                </div>
              )}
            </div>
            
            {/* Left - Name/Series */}
            <div style={{ 
              position: 'absolute', width: cubeSize, height: cubeSize,
              transform: `rotateY(-90deg) translateZ(${translateZ}px)`,
              background: '#12121e', border: '2px solid rgba(249,115,22,0.3)',
              borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 8, backfaceVisibility: 'hidden'
            }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 11, textAlign: 'center', lineHeight: 1.2 }}>{collectible.name}</span>
              <span style={{ color: '#6b7280', fontSize: 9, marginTop: 4 }}>{collectible.series}</span>
            </div>
            
            {/* Bottom - Price/Value */}
            <div style={{ 
              position: 'absolute', width: cubeSize, height: cubeSize,
              transform: `rotateX(-90deg) translateZ(${translateZ}px)`,
              background: '#12121e', border: '2px solid rgba(249,115,22,0.3)',
              borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              backfaceVisibility: 'hidden'
            }}>
              <span style={{ color: '#6b7280', fontSize: 9, marginBottom: 4 }}>VALUE</span>
              <span style={{ color: '#22c55e', fontWeight: 900, fontSize: cubeSize * 0.18 }}>${collectible.metal_value?.toFixed(2) || collectible.price?.toFixed(2) || '0.00'}</span>
              <span style={{ color: '#4b5563', fontSize: 8, marginTop: 2 }}>{collectible.metal_type === 'gold' ? 'Gold Line' : 'Silver Line'}</span>
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <span style={{ color: '#4b5563', fontSize: 9 }}>Spin • Tap to view</span>
        </div>
      </div>

      {/* Video Popup */}
      {showVideoPopup && collectible.video_url && (
        <div 
          style={{ position: 'fixed', inset: 0, background: 'rgba(13,13,26,0.95)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowVideoPopup(false)}
        >
          <div style={{ position: 'relative', maxWidth: 500, width: '100%' }} onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setShowVideoPopup(false)}
              style={{ position: 'absolute', top: -40, right: 0, color: '#fff', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X size={28} />
            </button>
            <div style={{ borderRadius: 16, overflow: 'hidden', border: '2px solid #F97316', boxShadow: '0 0 40px rgba(249,115,22,0.3)' }}>
              <video src={getMediaUrl(collectible.video_url)} autoPlay loop controls playsInline style={{ width: '100%' }} />
              <div style={{ position: 'absolute', top: 12, left: 12, background: '#dc2626', color: '#fff', fontWeight: 700, padding: '6px 12px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, background: '#fff', borderRadius: '50%', animation: 'pulse 1s infinite' }}></span> LIVE MOMENT
              </div>
              <div style={{ background: 'linear-gradient(to top, #000, transparent)', padding: 16, position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                <h3 style={{ color: '#fff', fontWeight: 900, fontSize: 18 }}>{collectible.name}</h3>
                <p style={{ color: '#9ca3af', fontSize: 14 }}>{collectible.series}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <span style={{ color: '#F97316', fontWeight: 900, fontSize: 24, textShadow: '0 0 10px currentColor' }}>#{collectible.edition_number}</span>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>of {collectible.total_editions}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Fullscreen Collectible Pop-out Modal with Video Support
const CollectiblePopOut = ({ collectible, onClose, metalPrices, onViewCertificate }) => {
  // Auto-play video if available (NBA Top Shot style)
  const [showVideo, setShowVideo] = useState(!!collectible.video_url);
  
  // Calculate total value with breakdown
  const valueData = calculateTotalValue(collectible, metalPrices);
  const serialTier = valueData.breakdown.serialTier;
  
  return (
    <ModalPortal>
    <div className="fixed inset-0 z-50 bg-[#12121e] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="relative max-w-2xl w-full animate-scale-in" onClick={e => e.stopPropagation()}>
        {/* Close button - prominent X */}
        <button 
          onClick={onClose} 
          className="absolute -top-2 -right-2 z-10 w-10 h-10 bg-[#12121e]/80 hover:bg-[#12121e] border border-gray-600 hover:border-orange-500 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-all"
        >
          <X size={24} />
        </button>
        
        {/* NBA Top Shot Style - Video First */}
        {showVideo && collectible.video_url ? (
          <div className="space-y-4">
            {/* Video Player - Full Width */}
            <div className="relative w-full rounded-2xl overflow-hidden border-4 border-orange-500 shadow-[0_0_60px_rgba(249,115,22,0.4)]">
              <video
                src={getMediaUrl(collectible.video_url)}
                autoPlay
                loop
                playsInline
                muted={false}
                controls
                className="w-full aspect-video object-cover"
              />
              {/* LIVE Badge */}
              <div className="absolute top-4 left-4 bg-red-600 text-white font-bold px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                LIVE MOMENT
              </div>
              {/* Serial Tier Badge */}
              {serialTier && (
                <div className={`absolute top-4 right-4 ${serialTier.bg} ${serialTier.color} font-bold px-4 py-2 rounded-full shadow-lg`}>
                  {serialTier.tier}
                </div>
              )}
              {/* Serial Number */}
              <div className="absolute bottom-4 right-4 bg-[#12121e]/80 rounded-lg px-4 py-2">
                <span className="text-amber-300 font-black text-2xl text-neon-glow">#{collectible.edition_number}</span>
                <span className="text-gray-400 text-sm ml-2">of {collectible.total_editions}</span>
              </div>
            </div>
            
            {/* Info Bar - Below Video */}
            <div className="bg-[#12121e] border border-orange-500/40 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-amber-400 font-bold text-xl">{collectible.name}</h3>
                  <p className="text-gray-500 text-sm">{collectible.series}</p>
                </div>
                <div className="flex items-center gap-4">
                  <RarityBadge rarity={collectible.rarity} />
                  {/* Total Value */}
                  <div className="text-right">
                    <span className="text-gray-500 text-xs block">Total Value</span>
                    <span className="text-green-400 font-bold text-xl">${valueData.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Value Breakdown Grid */}
              <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-800">
                <div className="text-center">
                  <span className="text-gray-500 text-xs block">{valueData.breakdown.metalType}</span>
                  <span className="text-white font-bold">${valueData.breakdown.metalValue?.toFixed(2)}</span>
                </div>
                <div className="text-center">
                  <span className="text-gray-500 text-xs block">Rarity ({valueData.breakdown.rarityMultiplier}x)</span>
                  <span className="text-amber-400 font-bold">+${valueData.breakdown.rarityBonus.toFixed(2)}</span>
                </div>
                <div className="text-center">
                  <span className="text-gray-500 text-xs block">Serial Bonus</span>
                  <span className="text-amber-300 font-bold">+${valueData.breakdown.serialBonus.toFixed(2)}</span>
                </div>
                <div className="text-center bg-green-500/10 rounded-lg py-1">
                  <span className="text-gray-500 text-xs block">Total</span>
                  <span className="text-green-400 font-bold">${valueData.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Fallback to Image if no video */
          <div className="space-y-4">
            <div className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden border-4 border-orange-500 shadow-[0_0_40px_rgba(249,115,22,0.3)]">
              <img src={getMediaUrl(collectible.image_url)} alt={collectible.name} className="w-full aspect-square object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent pointer-events-none"></div>
              <RarityBadge rarity={collectible.rarity} className="absolute top-3 right-3" />
              {/* Serial Tier Badge */}
              {serialTier && (
                <div className={`absolute top-3 left-3 ${serialTier.bg} ${serialTier.color} font-bold px-3 py-1 rounded-full text-sm`}>
                  {serialTier.tier}
                </div>
              )}
              <div className="absolute bottom-4 left-4">
                <div className="text-neon-glow text-amber-300 font-black text-4xl">#{collectible.edition_number}</div>
                <div className="text-gray-300 text-sm">of {collectible.total_editions}</div>
              </div>
            </div>
            
            {/* Details card */}
            <div className="bg-[#12121e] border border-orange-500/30 rounded-xl p-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-white font-bold text-xl">{collectible.name}</h3>
                  <p className="text-gray-500 text-sm">{collectible.series}</p>
                </div>
                <div className="text-right">
                  <span className="text-gray-500 text-xs block">Total Value</span>
                  <span className="text-green-400 font-bold text-xl">${valueData.total.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Value Breakdown */}
              <div className="grid grid-cols-4 gap-2 text-center mb-4">
                <div className="bg-[#12121e]/50 rounded-lg p-2 border border-gray-800">
                  <span className="text-gray-500 text-[10px] block">{valueData.breakdown.metalType}</span>
                  <span className="text-white font-bold text-sm">${valueData.breakdown.metalValue?.toFixed(2)}</span>
                </div>
                <div className="bg-[#12121e]/50 rounded-lg p-2 border border-amber-800/50">
                  <span className="text-gray-500 text-[10px] block">Rarity</span>
                  <span className="text-amber-400 font-bold text-sm">+${valueData.breakdown.rarityBonus.toFixed(2)}</span>
                </div>
                <div className="bg-[#12121e]/50 rounded-lg p-2 border border-orange-800/50">
                  <span className="text-gray-500 text-[10px] block">Serial</span>
                  <span className="text-amber-300 font-bold text-sm">+${valueData.breakdown.serialBonus.toFixed(2)}</span>
                </div>
                <div className="bg-green-500/10 rounded-lg p-2 border border-green-800/50">
                  <span className="text-gray-500 text-[10px] block">Total</span>
                  <span className="text-green-400 font-bold text-sm">${valueData.total.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <RarityBadge rarity={collectible.rarity} />
                  <span className="text-gray-400">×{valueData.breakdown.rarityMultiplier} multiplier</span>
                </div>
                {serialTier && (
                  <span className={`${serialTier.bg} ${serialTier.color} px-2 py-1 rounded text-xs font-bold`}>
                    {serialTier.tier}
                  </span>
                )}
              </div>
              
              {/* View Certificate Button */}
              {onViewCertificate && (
                <button
                  onClick={() => { onClose(); onViewCertificate(collectible); }}
                  className="w-full mt-4 bg-gradient-to-r from-orange-500/20 to-amber-500/20 hover:from-orange-500/30 hover:to-amber-500/30 border border-orange-500/50 text-amber-400 font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all"
                >
                  <Shield size={18} />
                  View Certificate of Authenticity
                </button>
              )}
              
              {collectible.price && (
                <div className="mt-4 pt-4 border-t border-gray-800 flex justify-between items-center">
                  <span className="text-gray-400">Listed Price</span>
                  <span className="text-amber-400 font-bold text-xl">${collectible.price}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <p className="text-gray-600 text-xs text-center mt-4">Tap anywhere to close</p>
      </div>
    </div>
    </ModalPortal>
  );
};

// PackOpeningAnimation is now imported from ./components/collectibles/PackOpeningAnimation

// ============== PAGES ==============

// 3D INTERACTIVE CUBE - NBA Top Shot Style with Drag/Swipe + Momentum

// RotatingCube extracted to /components/collectibles/RotatingCube.jsx

// Metal-Backed Value Section with LIVE Prices - Compact
const MetalBackedValue = () => {
  const [prices, setPrices] = useState({ 
    gold_blkback_price: 24.50, 
    silver_blkback_price: 20.07,
    last_updated: new Date().toISOString()
  });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await axios.get(`${API}/metal-prices`);
        setPrices(res.data);
      } catch (err) {
        console.error('Failed to fetch metal prices');
      }
      setLoading(false);
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 3600000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <section className="py-6 md:py-10 px-4 bg-[#12121e]">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-gray-600 text-[10px] uppercase">Live Data</span>
        </div>
        
        <h2 className="text-xl md:text-2xl font-black mb-2">
          <span className="text-white">CERTIFIED </span>
          <span className="text-amber-400">VALUE</span>
        </h2>
        
        <p className="text-gray-500 text-sm mb-4 max-w-lg">
          Crafted with real metallic elements, each BLK PACK comes certified and verified.
        </p>
        
        {/* Compact features + Live rates side by side on desktop */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Features */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Shield className="text-amber-400 shrink-0" size={16} />
              <span className="text-gray-400 text-sm">Certified & verified</span>
            </div>
            <div className="flex items-center gap-3">
              <Sparkles className="text-amber-400 shrink-0" size={16} />
              <span className="text-gray-400 text-sm">Limited serials</span>
            </div>
            <div className="flex items-center gap-3">
              <Users className="text-amber-400 shrink-0" size={16} />
              <span className="text-gray-400 text-sm">P2P swap spot</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Security & Purity Section - Premium Design
const SecurityPurity = () => {
  return (
    <section className="py-6 md:py-10 px-4 bg-[#12121e] relative overflow-hidden">
      {/* Background subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent pointer-events-none"></div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-4">
            <Shield className="text-amber-400" size={14} />
            <span className="text-amber-400 text-xs font-bold tracking-wider">CERTIFIED AUTHENTIC</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
            Security & <span className="text-amber-400">Quality</span>
          </h2>
          <p className="text-gray-400 text-base max-w-lg mx-auto">
            Crafted with real metallic elements, each BLK PACK comes certified and verified with anti-counterfeiting technology.
          </p>
        </div>
        
        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Gold Card */}
          <div className="group relative bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/40 rounded-2xl p-6 hover:border-orange-500/80 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                    <span className="text-white font-black text-xl">GOLD</span>
                  </div>
                  <div>
                    <h3 className="text-amber-400 font-black text-2xl">LEGENDARY</h3>
                    <p className="text-amber-400/60 text-sm">Premium Tier</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-4xl font-black text-white">A+</span>
                  <p className="text-gray-500 text-xs">GRADE</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Check className="text-green-500" size={16} />
                <span>Professionally graded collectible</span>
              </div>
            </div>
          </div>
          
          {/* Silver Card */}
          <div className="group relative bg-gradient-to-br from-gray-500/10 to-slate-600/5 border border-gray-500/40 rounded-2xl p-6 hover:border-gray-400/80 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gray-400/10 rounded-full blur-3xl"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-300 to-gray-500 rounded-xl flex items-center justify-center shadow-lg shadow-gray-400/30">
                    <span className="text-white font-black text-xl">STD</span>
                  </div>
                  <div>
                    <h3 className="text-white font-black text-2xl">STANDARD</h3>
                    <p className="text-gray-500 text-sm">Classic Tier</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-4xl font-black text-white">A</span>
                  <p className="text-gray-500 text-xs">GRADE</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Check className="text-green-500" size={16} />
                <span>Professionally graded collectible</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Anti-Counterfeiting Technology - Premium Design */}
        <div className="relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-500/5 rounded-full blur-[100px]"></div>
          </div>
          
          <div className="relative bg-[#12121e]/60 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            {/* Section Title */}
            <div className="text-center mb-8">
              <span className="text-gray-500 text-xs uppercase tracking-[0.3em]">Protection</span>
              <h3 className="text-xl font-black text-white mt-1">
                Anti-Counterfeiting <span className="text-amber-400">Technology</span>
              </h3>
            </div>
            
            {/* Security Features Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Shield, label: 'Tamper-Evident', desc: 'Holographic security seals', color: 'from-blue-500 to-amber-500', glow: 'blue' },
                { icon: Sparkles, label: 'UV Security', desc: 'Invisible light markers', color: 'from-amber-500 to-pink-500', glow: 'purple' },
                { icon: Search, label: 'Micro-Engraving', desc: 'Laser precision detail', color: 'from-orange-500 to-amber-500', glow: 'indigo' },
                { icon: Layers, label: 'Blockchain', desc: 'Verified digital registry', color: 'from-green-500 to-emerald-500', glow: 'green' }
              ].map((item, idx) => (
                <div key={idx} className="group relative">
                  <div className={`absolute inset-0 bg-${item.glow}-500/10 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none`}></div>
                  <div className="relative text-center p-4 bg-gray-900/50 border border-gray-800 rounded-xl hover:border-gray-700 transition-all duration-300">
                    <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className="text-white" size={22} />
                    </div>
                    <h4 className="text-white font-bold text-sm mb-1">{item.label}</h4>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Trust Badge */}
            <div className="mt-6 flex justify-center">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-2">
                <Shield size={14} className="text-amber-400" />
                <span className="text-amber-400 text-xs font-medium">Every Note Authenticated & Verified</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Claim Your Digital Banner Component - Enhanced with Marketplace & Receipt Upload
const ClaimDigitalBanner = () => {
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [marketplace, setMarketplace] = useState('');
  const [marketplaces, setMarketplaces] = useState([]);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [checkingSerial, setCheckingSerial] = useState(false);
  const [serialCheckResult, setSerialCheckResult] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [claimsRefresh, setClaimsRefresh] = useState(0);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Fetch marketplaces on mount
  useEffect(() => {
    axios.get(`${API}/claims/marketplaces`).then(res => {
      setMarketplaces(res.data);
    }).catch(() => {
      setMarketplaces(['eBay', 'Amazon', 'Etsy', 'Direct from BLKPacks', 'Other']);
    });

    // Track QR code scan if user arrived via QR
    const params = new URLSearchParams(window.location.search);
    const src = params.get('src');
    if (src === 'qr') {
      axios.post(`${API}/qr/track-scan?source=qr`).catch(() => {});
    }
  }, []);

  // Check serial availability when user types
  const checkSerialAvailability = async () => {
    if (!serialNumber.trim() || !token) return;
    setCheckingSerial(true);
    setSerialCheckResult(null);
    try {
      const formData = new FormData();
      formData.append('serial_number', serialNumber);
      const res = await authApi(token).post('/claims/check-serial', formData);
      setSerialCheckResult(res.data);
    } catch (err) {
      setSerialCheckResult({ available: false, message: err.response?.data?.detail || 'Error checking serial' });
    }
    setCheckingSerial(false);
  };

  // Handle receipt file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      setReceiptFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => setReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Upload receipt and submit claim
  const handleSubmitClaim = async (e) => {
    e.preventDefault();
    if (!user) {
      alert("Please sign in to claim your digital collectible");
      navigate('/login');
      return;
    }
    if (!serialNumber.trim()) {
      setResult({ success: false, message: 'Please enter a barcode number' });
      return;
    }
    if (!marketplace) {
      setResult({ success: false, message: 'Please select where you purchased the card' });
      return;
    }
    if (!receiptFile) {
      setResult({ success: false, message: 'Please upload a receipt or proof of purchase' });
      return;
    }
    
    setVerifying(true);
    setResult(null);
    
    try {
      // Step 1: Upload receipt image
      setUploading(true);
      const formData = new FormData();
      formData.append('file', receiptFile);
      const uploadRes = await authApi(token).post('/claims/upload-receipt', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploading(false);
      
      if (!uploadRes.data.url) {
        throw new Error('Failed to upload receipt');
      }
      
      // Step 2: Submit claim with receipt URL
      const claimRes = await authApi(token).post('/claims/submit', {
        serial_number: serialNumber,
        marketplace: marketplace,
        receipt_image_url: uploadRes.data.url,
        additional_notes: additionalNotes || null
      });
      
      setResult({ 
        success: true, 
        message: claimRes.data.message,
        matchFound: claimRes.data.match_found
      });
      
      // Reset form
      setSerialNumber('');
      setMarketplace('');
      setReceiptFile(null);
      setReceiptPreview(null);
      setAdditionalNotes('');
      setSerialCheckResult(null);
      setClaimsRefresh(prev => prev + 1);
      
      // Close modal after 3 seconds
      setTimeout(() => setShowClaimModal(false), 3000);
      
    } catch (err) {
      setResult({ 
        success: false, 
        message: err.response?.data?.detail || 'Failed to submit claim. Please try again.'
      });
    }
    setVerifying(false);
    setUploading(false);
  };

  return (
    <>
      <section id="claim-section" className="min-h-screen relative overflow-hidden bg-[#12121e]">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-orange-500/5 rounded-full blur-[200px]"></div>
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-orange-500/4 rounded-full blur-[150px]"></div>
          <div className="absolute top-1/3 left-0 w-[300px] h-[300px] bg-orange-600/3 rounded-full blur-[120px]"></div>
          {/* Grid overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
        </div>

        <div className="relative z-10 pt-24 md:pt-32 pb-16 px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Top Badge */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/25 px-5 py-2 rounded-full">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></div>
                <span className="text-amber-300 font-bold text-xs uppercase tracking-[0.25em]">Physical → Digital</span>
              </div>
            </div>

            {/* Hero Title */}
            <div className="text-center mb-6">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight mb-6">
                UNLOCK YOUR<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-400 to-orange-500">MOMENT.</span>
              </h1>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                Grabbed a physical BLK PACKS™ card from eBay, Amazon, or a retailer? Drop the warranty barcode number below to unlock your matching digital <span className="text-amber-300 font-semibold">Moment</span> and <span className="text-amber-300 font-semibold">Certificate of Authenticity</span>.
              </p>
            </div>

            {/* CTA Button */}
            <div className="flex flex-col items-center gap-4 mb-16">
              <button 
                onClick={() => user ? setShowClaimModal(true) : navigate('/login')}
                className="group relative inline-flex items-center gap-3"
                data-testid="open-claim-modal-btn"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-orange-500/40 to-amber-500/40 rounded-2xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative bg-gradient-to-r from-orange-500 to-amber-500 hover:from-amber-400 hover:to-amber-400 text-white font-black text-lg px-10 py-5 rounded-2xl transition-all flex items-center gap-3 tracking-wide">
                  <Package size={22} />
                  CLAIM BARCODE NUMBER
                </div>
              </button>
              
              {!user && (
                <p className="text-gray-500 text-sm flex items-center gap-1.5">
                  <Lock size={14} />
                  Sign in required to unlock your digital moment
                </p>
              )}
            </div>

            {/* My Claims status — pending / approved / rejected at a glance */}
            <MyClaimsStatus token={token} refreshKey={claimsRefresh} />

            {/* Steps - Bold Card Design */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Step 1 */}
              <div className="relative group">
                <div className="absolute -inset-[1px] bg-gradient-to-b from-orange-500/30 to-transparent rounded-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative bg-[#0c0c0c] rounded-2xl p-6 h-full border border-white/[0.04]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-black text-lg">1</span>
                    </div>
                    <div className="h-[1px] flex-1 bg-gradient-to-r from-orange-500/30 to-transparent"></div>
                  </div>
                  <h4 className="text-white font-bold text-sm mb-2">Drop Your Barcode</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">Enter the barcode number found on your physical BLK PACKS™ card.</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group">
                <div className="absolute -inset-[1px] bg-gradient-to-b from-orange-500/20 to-transparent rounded-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative bg-[#0c0c0c] rounded-2xl p-6 h-full border border-white/[0.04]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-500/20 border border-orange-500/40 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-300 font-black text-lg">2</span>
                    </div>
                    <div className="h-[1px] flex-1 bg-gradient-to-r from-orange-500/20 to-transparent"></div>
                  </div>
                  <h4 className="text-white font-bold text-sm mb-2">Verify Your Purchase</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">Upload your receipt or proof of purchase and we'll match your digital moment.</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group">
                <div className="absolute -inset-[1px] bg-gradient-to-b from-orange-500/10 to-transparent rounded-2xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative bg-[#0c0c0c] rounded-2xl p-6 h-full border border-white/[0.04]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-500/10 border border-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-300/70 font-black text-lg">3</span>
                    </div>
                    <div className="h-[1px] flex-1 bg-gradient-to-r from-orange-500/10 to-transparent"></div>
                  </div>
                  <h4 className="text-white font-bold text-sm mb-2">Unlock Everything</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">Receive your matching digital moment — then tap the certificate icon to claim your Certificate of Authenticity for both cards.</p>
                </div>
              </div>
            </div>

            {/* Bottom Accent */}
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-3 text-gray-600 text-xs uppercase tracking-[0.2em]">
                <div className="w-8 h-[1px] bg-gray-700"></div>
                Digital + Physical. Certified. Yours.
                <div className="w-8 h-[1px] bg-gray-700"></div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Claim Modal */}
      {showClaimModal && (
        <ModalPortal>
        <div className="fixed inset-0 bg-[#12121e]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-amber-500/30 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Package className="text-amber-500" size={24} />
                  Claim Your Digital
                </h2>
                <button onClick={() => setShowClaimModal(false)} className="text-gray-500 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmitClaim} className="space-y-4">
                {/* Barcode Number */}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Barcode Number *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={serialNumber}
                      onChange={(e) => {
                        setSerialNumber(e.target.value.toUpperCase());
                        setSerialCheckResult(null);
                      }}
                      placeholder="Enter barcode from your physical card"
                      className="input-field flex-1 bg-[#12121e]/50"
                      data-testid="claim-serial-input"
                    />
                    <button
                      type="button"
                      onClick={checkSerialAvailability}
                      disabled={checkingSerial || !serialNumber}
                      className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                      data-testid="check-serial-btn"
                    >
                      {checkingSerial ? <RefreshCw className="animate-spin" size={16} /> : 'Check'}
                    </button>
                  </div>
                  {serialCheckResult && (
                    <div className={`mt-2 p-2 rounded text-xs ${serialCheckResult.available ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                      {serialCheckResult.available && serialCheckResult.found_in_vault && serialCheckResult.collectible && (
                        <div className="flex items-center gap-2 mb-1">
                          <img src={getMediaUrl(serialCheckResult.collectible.image_url)} alt="" className="w-8 h-8 rounded object-cover" />
                          <span className="font-bold">{serialCheckResult.collectible.name}</span>
                        </div>
                      )}
                      {serialCheckResult.message}
                    </div>
                  )}
                </div>

                {/* Marketplace */}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Where did you purchase? *</label>
                  <select
                    value={marketplace}
                    onChange={(e) => setMarketplace(e.target.value)}
                    className="input-field w-full bg-[#12121e]/50"
                    data-testid="claim-marketplace-select"
                  >
                    <option value="">Select marketplace...</option>
                    {marketplaces.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                {/* Receipt Upload */}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Receipt / Proof of Purchase *</label>
                  <div className="border-2 border-dashed border-gray-700 hover:border-amber-500/50 rounded-lg p-4 text-center cursor-pointer transition-colors"
                    onClick={() => document.getElementById('receipt-upload').click()}>
                    <input
                      id="receipt-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="claim-receipt-input"
                    />
                    {receiptPreview ? (
                      <div className="relative">
                        <img src={receiptPreview} alt="Receipt" className="max-h-40 mx-auto rounded" />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setReceiptFile(null); setReceiptPreview(null); }}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="py-4">
                        <Upload className="mx-auto text-gray-500 mb-2" size={32} />
                        <p className="text-gray-500 text-sm">Click to upload receipt image</p>
                        <p className="text-gray-600 text-xs mt-1">PNG, JPG up to 10MB</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Additional Notes (Optional)</label>
                  <textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="Any details that might help verify your purchase..."
                    className="input-field w-full bg-[#12121e]/50 h-20 resize-none"
                    data-testid="claim-notes-input"
                  />
                </div>

                {/* Result Message */}
                {result && (
                  <div className={`p-3 rounded text-sm ${result.success ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                    {result.success && result.matchFound && <span className="font-bold">✓ Match Found! </span>}
                    {result.message}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={verifying || !serialNumber || !marketplace || !receiptFile}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-claim-btn"
                >
                  {verifying ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      {uploading ? 'Uploading Receipt...' : 'Submitting Claim...'}
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Submit Claim for Review
                    </>
                  )}
                </button>

                <p className="text-gray-500 text-xs text-center">
                  Our team will verify your receipt and transfer the digital collectible to your vault within 24-48 hours.
                </p>
              </form>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </>
  );
};


// HomePage extracted to /components/pages/HomePage.jsx

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResendLink, setShowResendLink] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [packPointsAcknowledged, setPackPointsAcknowledged] = useState(false);
  const [termsAcknowledged, setTermsAcknowledged] = useState(false);
  const { login, user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Read ?next= from the URL so we can return the user EXACTLY where they
  // tried to go before being intercepted by the auth gate. Previously this
  // file hardcoded navigate("/profile") on every success path, which is why
  // every protected click (Sell, Locker, etc.) dumped users on the profile
  // page after sign-in instead of the page they wanted.
  //
  // Whitelist target paths to internal routes only (must start with "/" and
  // can't contain "//" or ":" to block external/protocol-relative redirects).
  const rawNext = new URLSearchParams(location.search).get("next");
  const isSafeInternal = (p) =>
    typeof p === "string" &&
    p.startsWith("/") &&
    !p.startsWith("//") &&
    !p.includes(":") &&
    p.length < 256;
  // Default post-login landing is the Home page (`/`), NOT the Profile
  // page. Reason: Profile makes a secondary `/api/profile` fetch that
  // historically broke on iOS Capacitor bundles, leaving signed-in users
  // staring at a "Couldn't load profile" screen even though login itself
  // succeeded — making it feel like sign-in failed. Home renders from
  // public endpoints and is always usable. Users who explicitly came
  // from a protected route (`?next=/profile`) still get returned there.
  const nextPath = isSafeInternal(rawNext) ? rawNext : "/";

  // If user is already logged in, redirect away from login page (breaks login loops)
  useEffect(() => {
    if (user && token) {
      navigate(nextPath, { replace: true });
    }
  }, [user, token, navigate, nextPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setShowResendLink(false);
    try {
      const result = await login(email, password);
      // Check if user needs to verify email
      if (result && result.user) {
        if (!result.user.email_verified) {
          toast.info("Please verify your email to access all features", { duration: 5000 });
          navigate(nextPath);
        } else {
          navigate(nextPath);
        }
      } else {
        navigate(nextPath);
      }
    } catch (err) {
      // Extract the human-readable detail from the server. FastAPI
      // returns the message inside `response.data.detail` (string) or
      // sometimes a list of validation errors. Capacitor's iOS
      // Network bridge can occasionally drop the body, leaving us
      // with only `err.message = "Request failed with status code 403"`
      // — surface a sensible default in that case so users aren't
      // staring at an unhelpful axios string.
      let errorMsg = "Login failed";
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.trim()) {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        // Pydantic-validation array form
        errorMsg = detail[0].msg;
      } else if (status === 403) {
        // Body got eaten somewhere (Capacitor, ServiceWorker, etc.) —
        // 403 from /auth/login almost always means email-verification
        // gate. Surface the friendly copy + resend hint so the user
        // can self-recover.
        errorMsg = "Please verify your email address before logging in. Check your inbox for the verification link.";
      } else if (status === 401) {
        errorMsg = "Invalid email or password.";
      } else if (err?.response?.status === 0 || err?.message === "Network Error") {
        errorMsg = "Cannot connect to server. Please check your internet connection.";
      } else if (err?.message) {
        errorMsg = err.message;
      }
      console.error("[login] failure:", { status, detail, message: err?.message });
      setError(errorMsg);
      // Show resend link when the message is about email verification —
      // works whether the message came from the server or from our
      // 403-fallback default above.
      if (errorMsg.toLowerCase().includes("verify your email")) {
        setShowResendLink(true);
      }
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (!email) return;
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`);
      toast.success("Verification email sent! Check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to resend verification email"));
    }
    setResendLoading(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-32 px-4 flex items-center justify-center bg-[#12121e]">
      <div className="w-full max-w-md">
        <div className="bg-[#12121e] border border-gray-800 rounded-2xl p-6 md:p-8">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">Sign In</h1>
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
              {error}
              {showResendLink && (
                <button
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="block mt-2 text-amber-400 hover:text-amber-300 text-sm underline"
                >
                  {resendLoading ? "Sending..." : "Resend verification email"}
                </button>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                required
                data-testid="login-password-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <Link to="/forgot-password" className="text-amber-400 text-sm hover:underline font-medium" data-testid="forgot-password-link">
                Forgot password?
              </Link>
              <a href="mailto:support@blkpacks.fun?subject=Account%20Hacked%20-%20Need%20Help&body=I%20think%20my%20BLK%20PACKS%20account%20was%20hacked.%20My%20email%20is%3A%20" className="text-amber-400 text-sm hover:underline font-medium" data-testid="account-hacked-link">
                Account hacked?
              </a>
            </div>

            {/* 2026-06-08 — REMOVED the two consent checkboxes from the
                LOGIN page. They were copy-pasted from sign-up and were
                disabling the Sign In button by default. Existing users
                already accepted terms at sign-up. Login is now just
                email + password. */}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>

            {/* Face ID / Touch ID button — only on native iOS/Android */}
            {typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const { getCredentials: getBioCreds } = await import("./utils/nativeBridge");
                    const creds = await getBioCreds();
                    if (creds?.token) {
                      localStorage.setItem("blk_token", creds.token);
                      window.location.reload();
                    } else {
                      toast.info("No saved credentials. Sign in with email first.");
                    }
                  } catch {
                    toast.error("Biometric authentication failed");
                  }
                }}
                className="w-full mt-2 py-3 rounded-xl border border-orange-500/30 bg-orange-500/10 text-amber-400 font-bold text-sm flex items-center justify-center gap-2 hover:bg-orange-500/20 transition-colors"
                data-testid="biometric-login-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Sign in with Face ID
              </button>
            )}
          </form>
          <p className="text-center text-gray-400 mt-6">
            Don't have an account?{" "}
            <Link to="/register" className="text-amber-400 hover:underline">Register</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  // Recipient name for shipping labels — REQUIRED at signup so admin
  // never has to chase users for it later. See auth.py register() which
  // stashes these into shipping_address at account creation.
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [packPointsAcknowledged, setPackPointsAcknowledged] = useState(false);
  const [termsAcknowledged, setTermsAcknowledged] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Calculate age from DOB
  const getAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };
  
  const userAge = getAge(dateOfBirth);
  const isMinor = userAge !== null && userAge >= 9 && userAge < 18;
  const isTooYoung = userAge !== null && userAge < 9;

  // Check for referral code in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      // Validate the referral code
      axios.get(`${API}/referrals/check-code/${refCode}`)
        .then(res => setReferrerInfo(res.data))
        .catch(() => setReferrerInfo(null));
    }
  }, [location]);

  const handleResendVerification = async () => {
    if (!registeredEmail) return;
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(registeredEmail)}`);
      toast.success("Verification email sent! Check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to resend verification email"));
    }
    setResendLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isTooYoung) {
      setError("You must be at least 9 years old to create an account.");
      return;
    }
    if (!dateOfBirth) {
      setError("Date of birth is required.");
      return;
    }
    // First + last name required for shipping labels — enforce here so
    // fulfillment always has who to ship to.
    if (!firstName.trim() || !lastName.trim()) {
      setError("First and last name are required for shipping.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await register(email, password, username, referralCode || null, dateOfBirth, firstName.trim(), lastName.trim());
      if (result.referrer_message) {
        toast.success(result.referrer_message);
      }
      trackConversion('signup_complete');
      setRegisteredEmail(email);
      setRegistrationComplete(true);
    } catch (err) {
      let errorMsg = "Registration failed";
      if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.response?.status === 0 || err.message === "Network Error") {
        errorMsg = "Cannot connect to server. Please check your internet connection.";
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    }
    setLoading(false);
  };

  // Show email verification required screen after successful registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen pt-24 pb-32 px-4 flex items-center justify-center bg-[#12121e]">
        <div className="w-full max-w-md">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8 text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="text-green-400" size={40} />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-4">Verify Your Email</h1>
            
            <p className="text-gray-400 mb-6">
              We've sent a verification link to:
              <br />
              <span className="text-amber-300 font-semibold">{registeredEmail}</span>
            </p>
            
            {/* Instructions */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 mb-6 text-left">
              <h3 className="text-white font-bold text-sm mb-3">Next Steps:</h3>
              <ol className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white font-bold w-5 h-5 rounded-full flex items-center justify-center text-xs shrink-0">1</span>
                  <span>Check your email inbox (and spam folder)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white font-bold w-5 h-5 rounded-full flex items-center justify-center text-xs shrink-0">2</span>
                  <span>Click the verification link in the email</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white font-bold w-5 h-5 rounded-full flex items-center justify-center text-xs shrink-0">3</span>
                  <span>Complete your profile</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white font-bold w-5 h-5 rounded-full flex items-center justify-center text-xs shrink-0">4</span>
                  <span>Claim your FREE welcome pack!</span>
                </li>
              </ol>
            </div>
            
            {/* Resend Button */}
            <button
              onClick={handleResendVerification}
              disabled={resendLoading}
              className="w-full bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-xl transition-all mb-4"
              data-testid="resend-verification-btn"
            >
              {resendLoading ? "Sending..." : "Resend Verification Email"}
            </button>
            
            {/* Back to Login */}
            <p className="text-gray-500 text-sm">
              Already verified?{" "}
              <Link to="/login" className="text-amber-400 hover:underline">Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-32 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8">
          <h1 className="text-2xl font-bold text-white mb-6 text-center">Create Account</h1>
          
          {/* New Account Warning Banner */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="text-xl mt-0.5">⏰</div>
              <div>
                <div className="text-amber-400 font-bold text-sm">New Account Notice</div>
                <div className="text-amber-300/70 text-xs">To protect against bots, new accounts must wait 1 hour before joining drops. You can explore the Swap Spot and set up your profile during this time.</div>
              </div>
            </div>
          </div>
          
          {/* Trust messaging */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Shield className="text-blue-400 shrink-0 mt-0.5" size={20} />
              <div>
                <div className="text-blue-400 font-bold text-sm">Why We Verify Identity</div>
                <div className="text-blue-300/70 text-xs">BLK PACKS ships real gold and silver collectible cards. To prevent fraud and protect your Pack Points balance, We take security seriously. Your information is encrypted and never shared.</div>
              </div>
            </div>
          </div>
          
          {/* Referral Banner */}
          {referrerInfo && (
            <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="text-2xl">🎁</div>
                <div>
                  <div className="text-green-400 font-bold text-sm">Invited by {referrerInfo.referrer_username}</div>
                  <div className="text-green-300/70 text-xs">Purchase to unlock 5 bonus points for you both!</div>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Recipient name — required so fulfillment can print a label.
                Placed first so it feels like an account detail, not tacked-on. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-400 text-sm mb-2">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  className="input-field"
                  required
                  data-testid="register-first-name-input"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  className="input-field"
                  required
                  data-testid="register-last-name-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-field"
                required
                data-testid="register-username-input"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                required
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                required
                minLength={8}
                data-testid="register-password-input"
              />
              <p className="text-gray-500 text-xs mt-1">Minimum 8 characters</p>
            </div>
            
            {/* Date of Birth */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">Date of Birth</label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="input-field"
                required
                max={new Date().toISOString().split('T')[0]}
                data-testid="register-dob-input"
              />
              <p className="text-gray-500 text-xs mt-1">Must be 9 years or older</p>
            </div>
            
            {/* Age-based messaging */}
            {isTooYoung && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="text-red-400 font-bold text-sm">Too Young</div>
                <div className="text-red-300/70 text-xs">You must be at least 9 years old to create a BLK PACKS account. Ask a parent or guardian for help.</div>
              </div>
            )}
            
            {isMinor && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Shield className="text-amber-400 shrink-0 mt-0.5" size={18} />
                  <div>
                    <div className="text-amber-400 font-bold text-sm">Under 18 — Parent/Guardian Required</div>
                    <div className="text-amber-300/70 text-xs">Since you're under 18, a parent or guardian may need to verify the account for shipping purposes.</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Referral Code Field */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">Referral Code (optional)</label>
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Enter referral code"
                className="input-field font-mono tracking-widest"
                data-testid="register-referral-input"
              />
            </div>
            
            {/* Pack Points Acknowledgment */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={packPointsAcknowledged}
                    onChange={(e) => setPackPointsAcknowledged(e.target.checked)}
                    className="peer sr-only"
                    data-testid="register-pack-points-checkbox"
                  />
                  <div className="w-5 h-5 border-2 border-gray-600 rounded bg-gray-900 peer-checked:border-orange-500 peer-checked:bg-orange-500/20 transition-all flex items-center justify-center">
                    {packPointsAcknowledged && (
                      <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-gray-400 text-xs leading-relaxed group-hover:text-gray-300 transition-colors">
                  I understand that: <strong className="text-amber-300">Pack Points have no monetary value</strong>, digital collectibles are <strong className="text-amber-300">non-downloadable</strong> and remain on the platform, all swaps occur <strong className="text-amber-300">within the platform only</strong>, and BLK PACKS™ <strong className="text-amber-300">does not provide financial services</strong>.
                </span>
              </label>
            </div>
            
            {/* Terms & Legal Acknowledgment */}
            <div className="bg-orange-500/5 border border-orange-500/30 rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={termsAcknowledged}
                    onChange={(e) => setTermsAcknowledged(e.target.checked)}
                    className="peer sr-only"
                    data-testid="register-terms-checkbox"
                  />
                  <div className="w-5 h-5 border-2 border-orange-600/50 rounded bg-gray-900 peer-checked:border-orange-500 peer-checked:bg-orange-500/20 transition-all flex items-center justify-center">
                    {termsAcknowledged && (
                      <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-gray-400 text-xs leading-relaxed group-hover:text-gray-300 transition-colors">
                  I have read and agree to the <Link to="/terms" className="text-amber-400 hover:underline" target="_blank">Terms of Service</Link>, <Link to="/privacy" className="text-amber-400 hover:underline" target="_blank">Privacy Policy</Link>, and <Link to="/copyright" className="text-amber-400 hover:underline" target="_blank">Copyright & DMCA Policy</Link>. I understand that <span className="text-amber-300 font-semibold">BLK PACKS™</span> is a registered trademark and DBA of Xcraft FunWorks LLC, and that all products sold are collectibles for entertainment purposes only — not legal tender, currency, or financial instruments.
                </span>
              </label>
            </div>
            
            <button
              type="submit"
              disabled={loading || !packPointsAcknowledged || !termsAcknowledged}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>
          <p className="text-center text-gray-400 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-amber-400 hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};


// PlayPage extracted to /components/pages/PlayPage.jsx


// LockerPage extracted to /components/pages/LockerPage.jsx


// MarketplacePage extracted to /components/pages/MarketplacePage.jsx


// WalletPage extracted to /components/pages/WalletPage.jsx

// ============== CELEBRATION MODAL - ENHANCED ==============
const CelebrationModal = ({ data, onClose }) => {
  const audioRef = useRef(null);
  const [showFireworks, setShowFireworks] = useState(true);
  
  useEffect(() => {
    // Play hip-hop style celebration sounds
    if (data?.play_sound) {
      try {
        // Cash register / cha-ching sound
        const cashSound = new Audio('https://assets.mixkit.co/active_storage/sfx/2001/2001-preview.mp3');
        cashSound.volume = 0.5;
        cashSound.play().catch(() => {});
        
        // Add a bass drop after short delay
        setTimeout(() => {
          const bassSound = new Audio('https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3');
          bassSound.volume = 0.4;
          bassSound.play().catch(() => {});
        }, 300);
        
      } catch (e) {}
    }
    
    // Auto-close after animation
    const timer = setTimeout(() => {
      onClose();
    }, 6000);
    
    return () => clearTimeout(timer);
  }, [data, onClose]);
  
  if (!data) return null;
  
  return (
    <div className="fixed inset-0 bg-[#12121e]/95 flex items-center justify-center z-50 p-4" onClick={onClose}>
      {/* Golden particle effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Money/gold rain */}
        {[...Array(30)].map((_, i) => (
          <div
            key={`gold-${i}`}
            className="absolute text-2xl"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-10%`,
              animation: `confetti-fall ${2 + Math.random() * 3}s ease-in forwards`,
              animationDelay: `${Math.random() * 1}s`
            }}
          >
            💰
          </div>
        ))}
        
        {/* Confetti */}
        {data.show_confetti && [...Array(60)].map((_, i) => (
          <div
            key={`confetti-${i}`}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-5%`,
              width: `${8 + Math.random() * 12}px`,
              height: `${8 + Math.random() * 12}px`,
              backgroundColor: ['#FFD700', '#00FF00', '#FFFF00', '#FFA500', '#FF69B4', '#00CED1'][Math.floor(Math.random() * 6)],
              borderRadius: Math.random() > 0.5 ? '50%' : '0%',
              animation: `confetti-fall ${2.5 + Math.random() * 2}s ease-in forwards`,
              animationDelay: `${Math.random() * 0.8}s`,
              transform: `rotate(${Math.random() * 360}deg)`
            }}
          />
        ))}
        
        {/* Sparkle bursts */}
        {[...Array(12)].map((_, i) => (
          <div
            key={`spark-${i}`}
            className="absolute w-2 h-2 bg-amber-400 rounded-full"
            style={{
              left: `${20 + Math.random() * 60}%`,
              top: `${20 + Math.random() * 60}%`,
              animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
              animationDelay: `${Math.random() * 2}s`,
              boxShadow: '0 0 20px rgba(249,115,22,0.8)'
            }}
          />
        ))}
      </div>
      
      {/* Main celebration card */}
      <div 
        className="relative bg-[#12121e] border-4 border-orange-500 rounded-3xl p-8 max-w-lg w-full text-center animate-celebration-bounce"
        onClick={e => e.stopPropagation()}
        style={{
          boxShadow: '0 0 100px rgba(249,115,22,0.5), 0 0 50px rgba(0,255,0,0.3), inset 0 0 50px rgba(249,115,22,0.1)'
        }}
      >
        {/* Glowing border effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-green-500 to-orange-500 rounded-3xl blur-sm animate-pulse"></div>
        
        <div className="relative bg-[#12121e] rounded-3xl p-8">
          {/* Big money bag */}
          <div className="text-7xl mb-4 animate-bounce">💵</div>
          
          {/* Header */}
          <h2 
            className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-green-400 to-amber-300 mb-3 animate-glow-pulse"
            style={{ textShadow: '0 0 30px rgba(249,115,22,0.8)' }}
          >
            SOLD!
          </h2>
          
          {/* Collectible info */}
          <p className="text-xl text-white font-bold mb-1">
            {data.collectible_name}
          </p>
          <p className="text-lg text-amber-300 mb-4">
            Serial #{data.edition_number}
          </p>
          
          {/* Amount display - big and prominent */}
          <div className="bg-gradient-to-r from-green-900/50 to-green-800/50 border-2 border-green-500 rounded-2xl p-6 mb-6">
            <div className="text-gray-400 text-sm uppercase tracking-wider mb-1">You received</div>
            <div 
              className="text-5xl font-black text-green-400 animate-pulse"
              style={{ textShadow: '0 0 30px rgba(34,197,94,0.8)' }}
            >
              ${data.amount?.toFixed(2)}
            </div>
            <div className="text-gray-500 text-sm mt-2">Added to your Points</div>
          </div>
          
          {/* Buyer info */}
          <div className="text-gray-400 mb-6">
            Purchased by <span className="text-white font-bold">@{data.buyer_username}</span>
          </div>
          
          {/* Close button */}
          <button 
            onClick={onClose}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-black px-10 py-4 rounded-xl text-lg transition-all transform hover:scale-105"
            style={{ boxShadow: '0 0 30px rgba(249,115,22,0.5)' }}
          >
            LET'S GO! 🔥
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== EMAIL VERIFICATION PAGE ==============
const VerifyEmailPage = () => {
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  useEffect(() => {
    const verifyEmail = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link.');
        return;
      }

      try {
        const res = await axios.post(`${API}/auth/verify-email?token=${token}`);
        
        // Now the backend returns a token - log the user in!
        if (res.data.token) {
          localStorage.setItem("blk_token", res.data.token);
          // Refresh user context
          if (refreshUser) {
            await refreshUser();
          }
        }
        
        setStatus('success');
        setMessage(res.data.message || 'Email verified successfully!');
        
        // Redirect to Profile page for KYC after 2 seconds
        setTimeout(() => navigate('/profile'), 2000);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. Please try again.');
      }
    };

    verifyEmail();
  }, [location, navigate, refreshUser]);

  return (
    <div className="min-h-screen pt-24 px-4 flex items-center justify-center bg-[#12121e]">
      <div className="w-full max-w-md">
        <div className="bg-[#12121e] border border-gray-800 rounded-2xl p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <h1 className="text-xl font-bold text-white mb-2">Verifying Email...</h1>
              <p className="text-gray-400">Please wait while we verify your email address.</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-green-500" size={32} />
              </div>
              <h1 className="text-xl font-bold text-green-400 mb-2">Email Verified!</h1>
              <p className="text-gray-400 mb-4">{message}</p>
              
              {/* Next Steps */}
              <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 mb-4 text-left">
                <p className="text-amber-300 font-bold text-sm mb-2">Next Step: Complete Profile</p>
                <p className="text-gray-500 text-xs">Complete your profile to claim your FREE welcome pack!</p>
              </div>
              
              <p className="text-gray-500 text-sm">Redirecting to your profile...</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="text-red-500" size={32} />
              </div>
              <h1 className="text-xl font-bold text-red-400 mb-2">Verification Failed</h1>
              <p className="text-gray-400 mb-4">{message}</p>
              <Link to="/login" className="btn-primary inline-block">
                Go to Login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== FORGOT PASSWORD PAGE ==============
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(email)}`);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset link');
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen pt-24 px-4 flex items-center justify-center bg-[#12121e]">
        <div className="w-full max-w-md">
          <div className="bg-[#12121e] border border-gray-800 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="text-green-500" size={32} />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Check Your Email</h1>
            <p className="text-gray-400 mb-4">
              If an account exists with <span className="text-amber-400">{email}</span>, 
              we've sent a password reset link.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Check your spam folder if you don't see it within a few minutes.
            </p>
            <Link to="/login" className="text-amber-400 hover:underline">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 flex items-center justify-center bg-[#12121e]">
      <div className="w-full max-w-md">
        <div className="bg-[#12121e] border border-gray-800 rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-white mb-2 text-center">Forgot Password?</h1>
          <p className="text-gray-400 text-center mb-6">
            Enter your email and we'll send you a reset link.
          </p>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder="your@email.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
          
          <p className="text-center text-gray-400 mt-6">
            Remember your password?{' '}
            <Link to="/login" className="text-amber-400 hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// ============== RESET PASSWORD PAGE ==============
const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (!token) {
      setError('Invalid reset link');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password?token=${token}&new_password=${encodeURIComponent(password)}`);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen pt-24 px-4 flex items-center justify-center bg-[#12121e]">
        <div className="w-full max-w-md">
          <div className="bg-[#12121e] border border-gray-800 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-500" size={32} />
            </div>
            <h1 className="text-xl font-bold text-green-400 mb-2">Password Reset!</h1>
            <p className="text-gray-400 mb-4">Your password has been updated successfully.</p>
            <p className="text-gray-500 text-sm">Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 flex items-center justify-center bg-[#12121e]">
      <div className="w-full max-w-md">
        <div className="bg-[#12121e] border border-gray-800 rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-white mb-2 text-center">Reset Password</h1>
          <p className="text-gray-400 text-center mb-6">
            Enter your new password below.
          </p>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
              <p className="text-gray-500 text-xs mt-1">Minimum 8 characters required</p>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field"
                placeholder="Confirm your password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============== USER PROFILE PAGE ==============

// ProfilePage extracted to /components/pages/ProfilePage.jsx


// AdminPage extracted to /components/pages/AdminPage.jsx

// ============== CHAT PAGE ==============
const ChatPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [adminId, setAdminId] = useState(null);
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchConversations();
    fetchAdminId();
  }, [user]);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages();
      // Mark as read
      authApi(token).post(`/chat/mark-read/${selectedUser.user_id}`).catch(() => {});
    }
  }, [selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchAdminId = async () => {
    try {
      const res = await axios.get(`${API}/chat/admin-id`);
      setAdminId(res.data.admin_id);
    } catch (err) {}
  };

  const fetchConversations = async () => {
    try {
      const res = await authApi(token).get("/chat/conversations");
      setConversations(res.data);
    } catch (err) {}
    setLoading(false);
  };

  const fetchMessages = async () => {
    try {
      const res = await authApi(token).get("/chat/messages");
      // Filter messages for selected conversation
      const filtered = res.data.filter(m => 
        (m.from_user_id === selectedUser.user_id && m.to_user_id === user.id) ||
        (m.from_user_id === user.id && m.to_user_id === selectedUser.user_id)
      );
      setMessages(filtered.reverse());
    } catch (err) {}
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;
    
    setSending(true);
    try {
      await authApi(token).post("/chat/send", {
        recipient_id: selectedUser.user_id,
        message: newMessage.trim()
      });
      setNewMessage("");
      fetchMessages();
      fetchConversations();
    } catch (err) {
      alert("Failed to send message");
    }
    setSending(false);
  };

  const startAdminChat = () => {
    if (adminId) {
      setSelectedUser({ user_id: adminId, username: "Support" });
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white">Messages</h1>
          {!user.is_admin && adminId && (
            <button onClick={startAdminChat} className="btn-secondary flex items-center gap-2">
              <MessageCircle size={18} />
              Contact Support
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {/* Conversations List */}
          <div className="card md:col-span-1">
            <h2 className="text-white font-bold mb-4">Conversations</h2>
            {loading ? (
              <LoadingSpinner />
            ) : conversations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No conversations yet</p>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <button
                    key={conv.user_id}
                    onClick={() => setSelectedUser(conv)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedUser?.user_id === conv.user_id
                        ? "bg-orange-500/20 border border-orange-500/50"
                        : "bg-gray-800/50 hover:bg-gray-700/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium">@{conv.username}</span>
                      {conv.unread_count > 0 && (
                        <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm truncate">{conv.last_message}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chat Area */}
          <div className="card md:col-span-2 flex flex-col h-[500px]">
            {selectedUser ? (
              <>
                {/* Chat Header */}
                <div className="border-b border-gray-700 pb-3 mb-3">
                  <h3 className="text-white font-bold">@{selectedUser.username}</h3>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto space-y-3 mb-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.from_user_id === user.id ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`max-w-[70%] p-3 rounded-lg ${
                        msg.from_user_id === user.id
                          ? "bg-orange-500 text-white"
                          : "bg-gray-700 text-white"
                      }`}>
                        <p className="text-sm">{msg.message}</p>
                        <p className={`text-[10px] mt-1 ${
                          msg.from_user_id === user.id ? "text-black/60" : "text-gray-400"
                        }`}>
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 input-field"
                    data-testid="chat-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="btn-primary px-4"
                    data-testid="chat-send-btn"
                  >
                    <Send size={18} />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Select a conversation or contact support</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 24hr Notice */}
        <div className="mt-6 bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 text-center">
          <Clock className="inline mr-2 text-amber-400" size={18} />
          <span className="text-amber-400">Admin responds within 24 hours</span>
        </div>
      </div>
    </div>
  );
};


// ChallengesPage and MyRewardsTab extracted to /components/pages/ChallengesPage.jsx


// AdminChallengesTab extracted to /components/admin/AdminChallengesTab.jsx
// CalculatorPage extracted to /components/pages/CalculatorPage.jsx

// ============== LEGAL PAGES ==============
// Legal pages imported from ./pages/LegalPages

const ReportPage = () => {
  const [submitted, setSubmitted] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };
  
  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-6">Report Abuse</h1>
        
        {submitted ? (
          <div className="bg-green-500/20 border border-green-500 rounded-xl p-6 text-center">
            <Check className="mx-auto text-green-500 mb-4" size={48} />
            <h2 className="text-white font-bold text-xl mb-2">Report Submitted</h2>
            <p className="text-gray-400">Thank you for your report. Our team will review it within 24-48 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#12121e] border border-gray-800 rounded-xl p-6 space-y-4">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Type of Issue</label>
              <select className="input-field" required>
                <option value="">Select an issue type</option>
                <option value="fraud">Fraudulent Activity</option>
                <option value="harassment">Harassment</option>
                <option value="spam">Spam</option>
                <option value="copyright">Copyright Infringement</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Your Email</label>
              <input type="email" className="input-field" required placeholder="your@email.com" />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Description</label>
              <textarea className="input-field min-h-[120px]" required placeholder="Please describe the issue in detail..."></textarea>
            </div>
            <button type="submit" className="btn-primary w-full">Submit Report</button>
          </form>
        )}
      </div>
    </div>
  );
};

// ============== APP ==============

// Checkout Success Page for Coinbase Commerce redirects
const CheckoutSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const [result, setResult] = useState(null);
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chargeId = params.get('charge_id') || params.get('session_id');
    
    if (!chargeId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus(chargeId);
  }, [location]);

  const pollPaymentStatus = async (chargeId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      setStatus('pending');
      return;
    }
    
    try {
      const res = await authApi(token).get(`/payments/status/${chargeId}`);
      if (res.data.status === 'completed') {
        setStatus('success');
        setResult(res.data);
        refreshUser();
      } else if (res.data.status === 'expired' || res.data.status === 'failed') {
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(chargeId, attempts + 1), 2000);
      }
    } catch (err) {
      console.error(err);
      setTimeout(() => pollPaymentStatus(chargeId, attempts + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="card p-8">
            <RefreshCw className="w-16 h-16 mx-auto text-amber-400 animate-spin mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'pending' && (
          <div className="card p-8">
            <Clock className="w-16 h-16 mx-auto text-amber-400 mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Payment Processing</h1>
            <p className="text-gray-400 mb-4">Your payment is being processed. This may take a few moments.</p>
            <button onClick={() => navigate('/locker')} className="btn-primary">
              Go to Locker
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="card p-8">
            <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-gray-400 mb-6">Your purchase has been completed successfully.</p>
            <div className="flex flex-col gap-3">
              <button onClick={() => navigate('/locker')} className="btn-primary">
                View in Locker
              </button>
              <button onClick={() => navigate('/drops')} className="btn-secondary">
                Browse More Drops
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="card p-8">
            <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
              <X className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Failed</h1>
            <p className="text-gray-400 mb-6">Something went wrong with your payment. Please try again.</p>
            <button onClick={() => navigate('/drops')} className="btn-primary">
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ProfileCompleteGateWrapper — bridges the useAuth hook context to the
// gate component. Only renders when a logged-in user is missing shipping
// names. onComplete triggers refreshUser so App re-hydrates and the
// gate self-hides.
const ProfileCompleteGateWrapper = () => {
  const { user, token, refreshUser } = useAuth();
  if (!user || !token) return null;
  return <ProfileCompleteGate user={user} token={token} onComplete={refreshUser} />;
};

const SessionExpiredModal = () => {
  const ctx = useAuth();
  const sessionExpired = ctx?.sessionExpired;
  const setSessionExpired = ctx?.setSessionExpired;
  const logout = ctx?.logout;
  if (!sessionExpired || typeof setSessionExpired !== "function") return null;
  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="session-expired-modal">
      <div className="bg-[#1a1a2e] border border-red-500/40 rounded-xl max-w-sm w-full p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertCircle className="text-red-400" size={22} />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">Session Ended</h3>
            <p className="text-gray-400 text-xs">Your login has expired or is invalid.</p>
          </div>
        </div>
        <p className="text-gray-300 text-sm mb-5 leading-relaxed">
          Please log in again to continue. Your work is safe — you'll return here after.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setSessionExpired(false);
              if (typeof logout === "function") logout();
              window.location.href = "/login";
            }}
            className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-amber-400 hover:to-amber-400 text-white font-bold py-2.5 px-4 rounded-lg text-sm"
            data-testid="session-expired-relogin-btn"
          >
            Log In Again
          </button>
          <button
            onClick={() => setSessionExpired(false)}
            className="bg-white/5 hover:bg-white/10 text-gray-400 font-bold py-2.5 px-4 rounded-lg text-sm"
            data-testid="session-expired-dismiss-btn"
            title="Keep browsing (some features won't work until you log in)"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const perfMode = usePerformanceMode();

  // Silently detect new production deploys and force a one-shot reload so
  // users never get stuck on a stale JS bundle (which is what triggers
  // the "Something went wrong" ErrorBoundary right after a deploy).
  useVersionWatcher();

  // Mirror the "LIVE NOW" Wallet-pass counter onto the iOS app icon.
  // Refreshes on cold start, every 60s while foregrounded, and on resume.
  useLiveNowBadge();

  // Hide native Capacitor splash screen once React has mounted
  useEffect(() => {
    hideNativeSplash();
  }, []);

  // Surface 429 rate-limit responses as a soft toast (instead of a crash).
  // Backed by the global axios interceptor in utils.js which dispatches
  // a 'blk:rate-limit' CustomEvent whenever the server returns HTTP 429.
  useEffect(() => {
    const onRateLimit = (e) => {
      const retry = e?.detail?.retryAfter;
      toast.error(
        retry
          ? `Slow down — too many requests. Try again in ${retry}s.`
          : "Slow down — too many requests in a row.",
        { id: "rate-limit", duration: 4000 }
      );
    };
    window.addEventListener("blk:rate-limit", onRateLimit);
    return () => window.removeEventListener("blk:rate-limit", onRateLimit);
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App bg-[#12121e] min-h-[100dvh] flex flex-col" style={{ minHeight: '-webkit-fill-available' }} data-perf={perfMode}>
          {/* LaunchSplash REMOVED 2026-05-15: per user directive after 20+
              builds of chasing the "white box / logo splash" artifact. Three
              overlapping splash sources were all in play:
                1. iOS native Capacitor splash (LaunchScreen.storyboard) — killed
                2. Safari PWA apple-touch-startup-image tags → logo512.png — killed
                3. React <LaunchSplash /> overlay → blk-gradient-b — killed here
              App now boots straight into black → React mount with zero splash.
              Original component is preserved at components/common/LaunchSplash.jsx
              for future re-enable when proper per-device 1290×2796 splashes are pre-rendered. */}
          <Toaster
            position="top-center"
            richColors
            toastOptions={{
              className: "blk-toast",
              style: {
                background: "#0a0a12",
                color: "#fff",
                border: "1px solid rgba(249, 115, 22, 0.25)",
                borderLeft: "3px solid transparent",
                borderImage: "linear-gradient(180deg, #FCD34D 0%, #F97316 45%, #EF4444 80%, #DC2626 100%) 1",
                boxShadow: "0 8px 24px -4px rgba(239, 68, 68, 0.25), 0 4px 12px -2px rgba(0, 0, 0, 0.6)",
              },
            }}
          />
          <BuildVersionChecker />
          <SessionExpiredModal />
          <ProfileCompleteGateWrapper />
          <HelmetProvider>
          <BrowserRouter>
            <AuthLoadingGate>
            <ScrollToTop />
            <NavbarWrapper />
            <main className="flex-1 flex flex-col bg-[#12121e]" style={{ minHeight: '100vh', paddingTop: 'calc(48px + env(safe-area-inset-top, 0px))' }}>
              <MaintenanceGate>
              <Routes>
                {/* HomePage is directly loaded (not lazy) for PWA/TWA compatibility */}
                <Route path="/" element={<HomePageWrapper />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/signup" element={<Navigate to="/register" replace />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/claim" element={<ClaimYourCardPage />} />
                <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/verify" element={<Suspense fallback={<PageLoader />}><VerifyPageComponent /></Suspense>} />
                <Route path="/verify/tx/:txHash" element={<Suspense fallback={<PageLoader />}><TxDetailPageComponent /></Suspense>} />
                <Route path="/emerge/block/:height" element={<Suspense fallback={<PageLoader />}><EmergeBlockPage /></Suspense>} />
                <Route path="/emerge/tx/:hash" element={<Suspense fallback={<PageLoader />}><EmergeTxPage /></Suspense>} />
                <Route path="/emerge/drop/:dropId" element={<Suspense fallback={<PageLoader />}><EmergeDropPage /></Suspense>} />
                <Route path="/verify/pack/:id" element={<Suspense fallback={<PageLoader />}><VerifyPageComponent /></Suspense>} />
                <Route path="/verify/:id" element={<Suspense fallback={<PageLoader />}><VerifyPageComponent /></Suspense>} />
                <Route path="/copyright" element={<CopyrightPage />} />
                <Route path="/licensing" element={<LicensingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/shipping" element={<ShippingPage />} />
                <Route path="/whats-new" element={<WhatsNewPage />} />
                <Route path="/partnerships" element={<PartnershipsPage />} />
                <Route path="/report" element={<ReportPage />} />
                <Route path="/chat" element={<ChatPage />} />
                
                {/* Lazy-loaded routes wrapped in Suspense */}
                {/* PLAY = renamed Challenges page (formerly /challenges).
                    /play now hosts the trophy/compete hub. /drops hosts the
                    Drops list (previously at /play). Old /challenges and
                    /play→drops URLs redirect for back-compat with deployed
                    bundles + emailed links. Rename: 2026-06. */}
                <Route path="/play" element={<Suspense fallback={<PageLoader />}><ChallengesPageWrapper /></Suspense>} />
                <Route path="/drops" element={<Suspense fallback={<PageLoader />}><PlayPageWrapper /></Suspense>} />
                <Route path="/challenges" element={<Navigate to="/play" replace />} />
                <Route path="/sell" element={<Suspense fallback={<PageLoader />}><SellPageWrapper /></Suspense>} />
                <Route path="/scan" element={<Suspense fallback={<PageLoader />}><MonsterMeterPageComponent /></Suspense>} />
                {/* SEO funnel route — same scan flow, indexed for
                    "how much is my card worth" search intent */}
                <Route path="/value" element={<Suspense fallback={<PageLoader />}><MonsterMeterPageComponent /></Suspense>} />
                <Route path="/locker" element={<Suspense fallback={<PageLoader />}><LockerPageWrapper /></Suspense>} />
                <Route path="/boost-checkout" element={<Suspense fallback={<PageLoader />}><BoostCheckoutPageWrapper /></Suspense>} />
                <Route path="/marketplace" element={<Suspense fallback={<PageLoader />}><MarketplacePageWrapper /></Suspense>} />
                <Route path="/live" element={<Suspense fallback={<PageLoader />}><LiveBreaksPageWrapper /></Suspense>} />
                <Route path="/live/:dropId" element={<Suspense fallback={<PageLoader />}><LiveBreakRoomPageWrapper /></Suspense>} />
                {/* /rip/:cardId — dedicated AR pack-rip route. Used after Live
                    Break spot purchase and from notifications / share sheets. */}
                <Route path="/rip/:cardId" element={<Suspense fallback={<PageLoader />}><RipPageWrapper /></Suspense>} />
                {/* /host → redirect to /live. The host concept was deprecated
                    2026-02-23 — breaks are pre-recorded, so there's no human
                    host. Break Control (Go Live / End / Mark Pulled) is now
                    an admin-only overlay inside the Live Break room page. */}
                <Route path="/host" element={<Navigate to="/live" replace />} />
                <Route path="/host/:dropId" element={<HostRedirect />} />
                <Route path="/wallet" element={<Suspense fallback={<PageLoader />}><WalletPageWrapper /></Suspense>} />
                <Route path="/profile" element={<Suspense fallback={<PageLoader />}><ProfilePageWrapper /></Suspense>} />
                <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminPageWrapper /></Suspense>} />
                <Route path="/calculator" element={<Suspense fallback={<PageLoader />}><CalculatorPageComponent /></Suspense>} />
                {/* /challenges legacy route handled above as a Navigate.
                    See the /play block at the top of this Routes section. */}
                <Route path="/runit" element={<Suspense fallback={<PageLoader />}><RunItPageWrapper /></Suspense>} />
                <Route path="/runit/watch/:matchId" element={<Suspense fallback={<PageLoader />}><WatchMatchPageComponent /></Suspense>} />
                <Route path="/runit/card/:id/history" element={<Suspense fallback={<PageLoader />}><CardBattleHistoryPageComponent /></Suspense>} />
                <Route path="/runit/tournaments" element={<Suspense fallback={<PageLoader />}><TournamentsPageWrapper /></Suspense>} />
                <Route path="/runit/tournaments/:id" element={<Suspense fallback={<PageLoader />}><TournamentsPageWrapper /></Suspense>} />
                <Route path="/staking" element={<Suspense fallback={<PageLoader />}><StakingPageWrapper /></Suspense>} />
                <Route path="/sets" element={<Suspense fallback={<PageLoader />}><MySetsPageWrapper /></Suspense>} />
                <Route path="/series" element={<Suspense fallback={<PageLoader />}><SeriesPageWrapper /></Suspense>} />
                <Route path="/events" element={<Suspense fallback={<PageLoader />}><EventsPageWrapper /></Suspense>} />
                <Route path="/first-rip" element={<Suspense fallback={<PageLoader />}><FirstRipPageWrapper /></Suspense>} />
                <Route path="/drops/:dropId" element={<Suspense fallback={<PageLoader />}><DropDetailPageComponent /></Suspense>} />
                <Route path="/activity" element={<Suspense fallback={<PageLoader />}><ActivityFeedPageWrapper /></Suspense>} />
                <Route path="/user/:userId" element={<Suspense fallback={<PageLoader />}><PublicProfilePageWrapper /></Suspense>} />
                
                {/* Catch-all route - if nothing matches, show HomePage */}
                <Route path="*" element={<HomePageWrapper />} />
              </Routes>
              </MaintenanceGate>
              
              {/* Emergency fallback - shows briefly while routes initialize */}
              <noscript>
                <div className="min-h-screen bg-[#12121e] text-white flex items-center justify-center">
                  <p>Please enable JavaScript to use BLK PACKS™</p>
                </div>
              </noscript>
            </main>
            <MobileBottomNavWrapper />
            <Footer />
            {/* GetAppBanner removed 2026-05-15 per user directive. The
                "Get the App / Rip. Reveal. Play." bar was rendering on
                every page load (signed in, signed out, every view) and
                being perceived as an intro splash. Domain should land
                directly on the home page with zero nag overlays. */}
            </AuthLoadingGate>
          </BrowserRouter>
          </HelmetProvider>
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
